import { EMPTY_ARRAY_LENGTH, FIRST_ELEMENT_INDEX, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    AccountMappingAccountItem,
    AccountMappingTemplateGroupItem,
    AccountType,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    IBalanceSwitchAccount,
    IFinancialStatementInterpretation,
    IFinStatements,
    IManualAddedOrUpdatedAccount,
    IReclassesAccounts,
    IReclassesAndAdjustmentExtension,
    IReclassificationAccountRef,
    IReclassificationTransaction,
    ItemType,
    NumberFormats,
    ReclassificationPeriod,
    ReclassificationStatement,
    Signs,
    SortedMappingItem,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IAccountMappingMap } from '@modeso/types__aumico-lib-annualreport-be/dist/models/interfaces/IAccountMappingMap.interface';
import { FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import { DefaultGroupsNames, ITemplateEntityDTO, TemplateType } from '@modeso/types__aumico-lib-templates-be';
import Debug from 'debug';
import { cloneDeep, differenceWith } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { defaultSystemRules } from '../constants/defaultSystemRules.constant';
import { EngineModes } from '../enums/engineModes.enum';
import { AccountMappingEngineHelper } from '../helpers/accountMappingEngineHelper';
import { ICalculation } from '../interfaces/ICalculation.interface';
import { IGroupParentGroupAndIndexes } from '../interfaces/group-parent-group-and-indexes';
import { IItemParentGroupAndIndexes } from '../interfaces/item-parent-group-and-indexes.interface';
import { MappingEngineAccount } from '../interfaces/mapping-engine-account.interface';
import { ISplitAccountValues } from '../interfaces/split-account-values.interface';
import { IUncoveredShortfallSpecifications } from '../interfaces/uncovered-shortfall-specifications.interface';
import {
    AccountOrigin,
    BalanceSwitchAccountService,
    MergedAccount,
    SplittedAccount,
    SwitchAccountMappingResults,
} from '../services/balance-switch-account.service';
import {
    isAccount,
    isEmpty,
    isEmptyRowGroup,
    isFli,
    isGroup,
    isHeader,
    isHeaderGroup,
    isOfThatItem,
    isSubfli,
    isSubTotal,
} from '../type-guards/type-guards';
import { getFlipRule, mapToAccountMappingTemplate } from './account-mapping-template.mapper';

const debug = Debug('aumico:aumico-lib-templates-fe:AccountMappingEngine');

const IncomeTotalsAccountIndex = 0;
interface IBalanceSwitchAdjustment {
    accountsToFix: IAccount[]; // Accounts needing the balance update
    accountsToZero: IAccount[]; // Accounts needing to be cleared
}


export class AccountMappingEngine {
    private _stateChangedSubject = new BehaviorSubject({});
    private accountsDictionaryWithAccountNumberAsKey: { [key: string]: IAccount };
    private accountsDictionaryWithAccountDataAsKey: { [key: string]: IAccount };
    private accountsDictionaryWithAccountDataWithoutBalanceAsKey: { [key: string]: IAccount };
    private accountsDictionaryWithAccountDataWithOpeningBalanceAsKey: { [key: string]: IAccount };
    private accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKey: { [key: string]: IAccount };
    private transferredProfitAccountName: string;
    private _selectedAccounts: IAccount[] = [];
    private _unSelectedAccounts: IAccount[] = [];
    private _draggedAccounts: IAccount[] = [];
    private _allFlis: IAccountMappingTemplateGroupItemFLI[] = [];
    private duplicateAccountNumberSet: Set<string>;
    private mergedAccountsMap: Map<string, IAccount>;
    private profitOfTheYearFliName: string;
    private annualSurplusFliName: string | undefined;
    private masterCloneAccountsToNumberOfCloneAccountsMap: Map<IAccount, number> = new Map<IAccount, number>();
    private balanceSwitchAccountService: BalanceSwitchAccountService;
    private previousSwitchAccounts: IBalanceSwitchAccount[];
    createSplitAccountsAutomaticallyDone: boolean = false;
    balanceSwitchAdjustments = new Map<string, IBalanceSwitchAdjustment>();

    /**
     * REV-20899: transactional reclassifications. When set with version 1,
     * the engine derives every account's `openingReClass` / `closingReClass`
     * from this list at the top of every `calculateAll()` pass.
     */
    private reclassTransactions: IReclassificationTransaction[] = [];
    private reclassStatement: ReclassificationStatement | undefined;
    private reclassVersion: 0 | 1 | undefined;

    constructor(
        private accounts: IAccount[],
        private templateMapping: IAccountMappingTemplate,
        private balanceSwitchAccounts: IBalanceSwitchAccount[],
        private sheetSystemRules: IFinancialStatementInterpretation,
        private dates: {
            startDate: string;
            endDate: string;
        },
        private mode: EngineModes,
        private previousDuplicateAccountNumber?: Set<string>,
        private uncoveredShortfallSpecifications?: IUncoveredShortfallSpecifications,
        private previousYearTemplate?: IAccountMappingTemplate, // FROM DEV
        private resetTemplateBalancesToZero?: boolean,        // FROM STAGING
        private copiedFinStatement?: IFinStatements,      // FROM STAGING
    ) {

        this.accounts = this.accounts.filter((account) => account != null);

        if (this.resetTemplateBalancesToZero === true) {
            // we reset the balances to 0, this is when we copy from other report and want to keep the mapping but reset balances
            // We cannot map based on balance anyways in this case
            this.resetTemplateBalances(this.templateMapping.group);
        }
        this.dates = {
            startDate: dates.startDate,
            endDate: dates.endDate,
        };
        // If modes is COPY_FROM_OTHER_REPORT then we need to filter accounts which are coming from previous report only
        // we need to remove them from templateMapping
        if (mode === EngineModes.COPY_FROM_OTHER_REPORT && templateMapping != null) {

            this.initEngineForCopyFromOtherReport();
            return;
        }
        this.balanceSwitchAccountService = new BalanceSwitchAccountService();

        let switchAccountMappingResults: SwitchAccountMappingResults = undefined;
        if (this.mode === EngineModes.MANUAL && this.getTopGroup().name === DefaultGroupsNames.BALANCE_SHEET) {
            // this is for the case where mapping was already saved but accounts array was updated due to user reuploading balance sheet
            switchAccountMappingResults = this.recreateSplitAccounts(false);
        }

        if (
            (this.mode === EngineModes.AUTOFILL ||
                this.mode === EngineModes.PREVIOUS_AUTOMATIC_FILL ||
                this.mode === EngineModes.COPY_FROM_OTHER_REPORT) &&
            this.getTopGroup().name === DefaultGroupsNames.BALANCE_SHEET
        ) {
            // check if there were any balance switch accounts in the previous year
            switchAccountMappingResults = this.recreateSplitAccounts(false);

            // --- SYNC THE TEMPLATE MAPPING Duplicate Accounts ---
            // This ensures your working object (templateMapping) knows about the resolutions
            // that were just applied from the source (copiedFinStatement).
            if (this.previousYearTemplate && this.previousYearTemplate.duplicateAccountResolutions) {
                // We use cloneDeep to ensure we don't accidentally mutate the source if we edit later
                this.templateMapping.duplicateAccountResolutions = cloneDeep(this.previousYearTemplate.duplicateAccountResolutions);
            }
        }

        // should have the duplicate first then break accounts in previous automatic fill mode
        this.prepareDuplicateAccountSet();

        //Automap split accounts
        if (switchAccountMappingResults && switchAccountMappingResults.splittedAccounts.length > 0) {
            const validSplittedAccounts = switchAccountMappingResults.splittedAccounts.filter(
                (splittedAccount: SplittedAccount) => splittedAccount?.balanceSwitchAccount?.accountNumber != null,
            );

            //map splitted accounts
            if (this.mode === EngineModes.COPY_FROM_OTHER_REPORT || this.mode === EngineModes.PREVIOUS_AUTOMATIC_FILL) {
                // break account which was duplicate in previous year but single in current year
                this.breakPreviousYearDuplicateCurrentYearSingleAccounts();
            }

            //filter this.accounts to get the splitted accounts
            const splittedAccountsFiltered = this.accounts.filter((account) => {
                if (account?.accountNumber == null) {
                    return false;
                }
                return validSplittedAccounts.some((splittedAccount: SplittedAccount) => {
                    return account.accountNumber === splittedAccount?.balanceSwitchAccount?.accountNumber;
                });
            });

            // Loop through splitted accounts and map them to the correct group
            for (let index = 0; index < splittedAccountsFiltered.length; index++) {
                const account = splittedAccountsFiltered[index];
                if (account?.accountNumber == null) {
                    continue;
                }

                // get balance switch accounts from splitted accounts
                const balanceSwitchAccount: IBalanceSwitchAccount | undefined =
                    validSplittedAccounts.find((splittedAccount: SplittedAccount) => {
                        return account.accountNumber === splittedAccount?.balanceSwitchAccount?.accountNumber;
                    })?.balanceSwitchAccount;

                if (balanceSwitchAccount == null) {
                    continue;
                }
                // determin if this account is Asset or Liabilities account
                let accountOriginFromAssets = (account as unknown as IBalanceSwitchAccount)?.originsFromAsset;
                if (accountOriginFromAssets) {
                    //find and replace here: templateMapping.group.groups[0];
                    this.findAccountToUpdateInGroup(account, DefaultGroupsNames.ASSETS);
                    continue;
                } else {
                    //find and replace here: templateMapping.group.groups[1];
                    this.findAccountToUpdateInGroup(account, DefaultGroupsNames.LIABILITIES);
                    continue;
                }
            }
        }

        // STAGING HOTFIX: Check if there is any balance switch account left on the raw accounts side
        const adjustments = new Map<string, IBalanceSwitchAdjustment>();

        if (this.getTopGroup().name === DefaultGroupsNames.BALANCE_SHEET &&
            this.balanceSwitchAccounts.length === EMPTY_ARRAY_LENGTH) {

            this.accounts.forEach((rawAccount) => {
                if (rawAccount == null) return;

                if (!rawAccount.isBalanceSwitchAccount) return;

                // Check both Asset and Liability matches
                [DefaultGroupsNames.ASSETS, DefaultGroupsNames.LIABILITIES].forEach(groupName => {
                    const match = this.findAccount(rawAccount, groupName);

                    if (match) {
                        const [mappedAccount] = match;
                        const accountKey = rawAccount.accountNumber; // Or whatever unique identifier connects them

                        // Initialize the entry in our map if it doesn't exist
                        if (!adjustments.has(accountKey)) {
                            adjustments.set(accountKey, { accountsToFix: [], accountsToZero: [] });
                        }

                        const entry = adjustments.get(accountKey)!;

                        let isMatch = Math.abs(rawAccount.openingBalance) === mappedAccount.calculatedClosingBalance;
                        const closingBalance = rawAccount.closingBalance;
                        if (mode === EngineModes.MANUAL) {
                            isMatch = Math.abs(rawAccount.openingBalance) === mappedAccount.calculatedOpeningBalance;
                        }
                        if (mode === EngineModes.AUTOFILL) {
                            // NOT IMPLEMENTED RIGHT NOW, NOT USED ANYMORE
                        }
                        if (mode === EngineModes.PREVIOUS_AUTOMATIC_FILL || mode === EngineModes.COPY_FROM_OTHER_REPORT) {
                            // keep current isMatch logic
                        }

                        if (isMatch) {
                            mappedAccount["markForBalanceSwitchAccountFix"] = true;
                            mappedAccount["markForBalanceSwitchAccountFixClosingBalance"] = closingBalance;
                            entry.accountsToFix.push(mappedAccount);
                        } else {
                            entry.accountsToZero.push(mappedAccount);
                        }
                    }
                });
            });
        }

        this.balanceSwitchAdjustments = adjustments;
        this.calculateAll(true);
    }

    private applyDuplicateAccountResolutions(): void {

        const resolutions = this.copiedFinStatement?.templateAndAccountsMapping.template.duplicateAccountResolutions;
        if (!resolutions) return;

        // 1. Identify which account numbers need summarizing
        const accountNumbersToSummarize = resolutions
            .filter(r => r.resolution === 'summarized')
            .map(r => r.accountNumber);

        // 2. Separate accounts into those being summarized and those staying as-is
        const normalAccounts = this.accounts.filter(acc => !accountNumbersToSummarize.includes(acc.accountNumber));
        const accountsToProcess = this.accounts.filter(acc => accountNumbersToSummarize.includes(acc.accountNumber));

        // 3. Create the summarized versions
        const summarizedAccounts: IAccount[] = accountNumbersToSummarize.map(accNum => {
            const group = accountsToProcess.filter(a => a.accountNumber === accNum);
            if (group.length <= 1) return group[0]; // Or handle as needed

            return {
                ...cloneDeep(group[0]),
                openingBalance: NumberUtils.roundNumber(group.reduce((s, a) => s + (a.openingBalance || 0), 0)),
                closingBalance: NumberUtils.roundNumber(group.reduce((s, a) => s + (a.closingBalance || 0), 0)),
                isSummarized: true,
                summarizedFrom: cloneDeep(group),
            };
        }).filter(acc => !!acc);

        // 4. Update the state once
        this.accounts = [...normalAccounts, ...summarizedAccounts];

        // This ensures your working object (templateMapping) knows about the resolutions
        // that were just applied from the source (copiedFinStatement).
        if (this.templateMapping) {
            // We use cloneDeep to ensure we don't accidentally mutate the source if we edit later
            this.templateMapping.duplicateAccountResolutions = cloneDeep(resolutions);
        }

    }

    public summarizeAccounts(accountNumber: string): void {
        const accountsToSummarize = this.accounts.filter((acc) => acc.accountNumber === accountNumber);

        if (accountsToSummarize.length < 2) {
            debug(`No accounts or only one account found for number ${accountNumber}, summarization skipped.`);
            return;
        }

        // 1. Ensure all accounts are unmapped
        const isAnyAccountMapped = accountsToSummarize.some((acc) =>
            this._selectedAccounts.some((selected) => this.areAccountsEqual(acc, selected)),
        );

        if (isAnyAccountMapped) {
            throw new Error('Cannot summarize accounts because at least one is already mapped.');
        }

        // 2. Create the summarized account
        const summarizedAccount: IAccount = {
            ...cloneDeep(accountsToSummarize[0]), // Use first account as a template
            openingBalance: NumberUtils.roundNumber(
                accountsToSummarize.reduce((sum, acc) => sum + (acc.openingBalance || 0), 0),
            ),
            closingBalance: NumberUtils.roundNumber(
                accountsToSummarize.reduce((sum, acc) => sum + (acc.closingBalance || 0), 0),
            ),
            isSummarized: true,
            summarizedFrom: cloneDeep(accountsToSummarize), // Store original accounts
        };

        // 3. Replace original accounts with the summarized one
        this.accounts = this.accounts.filter((acc) => acc.accountNumber !== accountNumber);
        this.accounts.push(summarizedAccount);

        // 4. Persist the decision
        if (!this.templateMapping.duplicateAccountResolutions) {
            this.templateMapping.duplicateAccountResolutions = [];
        }
        const resolutionIndex = this.templateMapping.duplicateAccountResolutions.findIndex(
            (res) => res.accountNumber === accountNumber,
        );
        if (resolutionIndex > -1) {
            this.templateMapping.duplicateAccountResolutions[resolutionIndex].resolution = 'summarized';
        } else {
            this.templateMapping.duplicateAccountResolutions.push({ accountNumber, resolution: 'summarized' });
        }
        this.calculateAll(false);
    }

    public unsummarizeAccount(accountNumber: string): void {
        // Find ALL accounts with this number that are summarized. This will find both halves of a switch account.
        const summarizedParts = this.accounts.filter(
            (acc) => acc.accountNumber === accountNumber && acc.isSummarized,
        );

        if (summarizedParts.length === 0) {
            debug(`No summarized account found for number ${accountNumber}, un-summarization skipped.`);
            return;
        }

        // Check if any of the parts are mapped.
        const isAnyPartMapped = summarizedParts.some((part) =>
            this._selectedAccounts.some((selected) => this.areAccountsEqual(part, selected)),
        );

        if (isAnyPartMapped) {
            throw new Error('Cannot un-summarize an account that is already mapped.');
        }

        // All parts should have the same original data. Take it from the first one.
        const originalAccounts = summarizedParts[0].summarizedFrom;
        if (!originalAccounts || originalAccounts.length === 0) {
            throw new Error('Cannot un-summarize account: original account data is missing.');
        }

        // 1. Remove ALL summarized parts (both halves of the switch account).
        this.accounts = this.accounts.filter((acc) => !(acc.accountNumber === accountNumber && acc.isSummarized));

        // 2. Restore the original accounts.
        this.accounts.push(...originalAccounts);

        // 3. Update the persisted decision.
        if (this.templateMapping.duplicateAccountResolutions) {
            this.templateMapping.duplicateAccountResolutions = this.templateMapping.duplicateAccountResolutions.filter(
                (res) => res.accountNumber !== accountNumber,
            );
        }
        this.calculateAll(false);
    }

    public keepAccountsSeparate(accountNumber: string): void {
        if (!this.templateMapping.duplicateAccountResolutions) {
            this.templateMapping.duplicateAccountResolutions = [];
        }

        const resolutionIndex = this.templateMapping.duplicateAccountResolutions.findIndex(
            (res) => res.accountNumber === accountNumber,
        );

        if (resolutionIndex > -1) {
            this.templateMapping.duplicateAccountResolutions[resolutionIndex].resolution = 'separate';
        } else {
            this.templateMapping.duplicateAccountResolutions.push({ accountNumber, resolution: 'separate' });
        }
        debug(`Accounts with number ${accountNumber} will be kept separate.`);
        this._stateChangedSubject.next({});
    }

    // Helper from Staging for Hotfixes
    private findAccount(
        account: IAccount,
        groupName = DefaultGroupsNames.ASSETS
    ): [IAccount, IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI] | undefined {
        const group = this.getTopGroup().groups.find((group) => group.name === groupName);
        if (!group) return undefined;

        const items: (IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI)[] = [];
        for (const item of group.sortedItems) {
            if (isFli(item as SortedMappingItem)) {
                const fli = item as IAccountMappingTemplateGroupItemFLI;
                items.push(fli);
                items.push(...this.getSubFlisOfFli(fli));
            }
            if (isGroup(item as SortedMappingItem)) {
                items.push(...this.getFlisAndSubFlisOfGroups(item as IAccountMappingTemplateGroup));
            }
        }

        for (const container of items) {
            const foundAccount = container.accounts.find(
                (acc) => isAccount(acc) && (acc as IAccount).accountNumber === account.accountNumber
            ) as IAccount;

            if (foundAccount) {
                // Returning the Tuple: [Account, Container]
                return [foundAccount, container];
            }
        }

        return undefined;
    }

    private findAccountToUpdateInGroup(
        account: IAccount,
        groupName = DefaultGroupsNames.ASSETS,
        remove: boolean = false,
    ): void {
        const group = this.getTopGroup().groups.find((group) => group.name === groupName);
        const items: (IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI)[] = [];
        for (const item of group.sortedItems) {
            if (isFli(item as SortedMappingItem)) {
                items.push(item as IAccountMappingTemplateGroupItemFLI);
                items.push(...this.getSubFlisOfFli(item as IAccountMappingTemplateGroupItemFLI));
            }
            if (isGroup(item as SortedMappingItem)) {
                items.push(...this.getFlisAndSubFlisOfGroups(item as IAccountMappingTemplateGroup));
            }
        }

        const potentialFlis = items.filter((item) => !isSubfli(item)) as IAccountMappingTemplateGroupItemFLI[];
        const potentialSubflis = items.filter((item) => isSubfli(item)) as IAccountMappingSubFLI[];

        for (const fli of potentialFlis) {
            const fliMatchingAccountIndex = fli.accounts.findIndex(
                (acc) => isAccount(acc) && (acc as IAccount).accountNumber === account.accountNumber,
            );

            if (fliMatchingAccountIndex > -1) {
                this.updateMappedSplitAccount(fli, account, remove);
            }
        }

        for (const subfli of potentialSubflis) {
            const matchingAccountIndex = subfli.accounts.findIndex(
                (acc) => acc.accountNumber === account.accountNumber,
            );
            if (matchingAccountIndex > -1) {
                this.updateMappedSplitAccount(subfli, account, remove);
            }
        }
    }

    private updateMappedSplitAccount(
        item: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        account: IAccount,
        remove: boolean,
    ): void {
        if (remove) {
            item.accounts = item.accounts.filter(
                (acc) =>
                    !((acc as IAccount).accountNumber === account.accountNumber && acc.isBalanceSwitchAccount === true),
            );
            return;
        }
        const accountInItem = item.accounts.find((acc) => (acc as IAccount).accountNumber === account.accountNumber);

        accountInItem.openingBalance = account.openingBalance;
        accountInItem.calculatedOpeningBalance = account.calculatedOpeningBalance;
        accountInItem.closingBalance = account.closingBalance;
        accountInItem.calculatedClosingBalance = account.calculatedClosingBalance;
        accountInItem.isBalanceSwitchAccount = account.isBalanceSwitchAccount;
        accountInItem.comingFromPreviousAnnualReport = false;
    }

    /**
     * Traverses the template mapping and resets the opening balances of all mapped accounts to zero.
     * Useful for scenarios where the mapping is carried over but the opening state must be cleared.
     * (FROM STAGING)
     */
    private resetTemplateBalances(group: IAccountMappingTemplateGroup): void {
        for (const item of group.sortedItems) {
            if (isFli(item as SortedMappingItem)) {
                const fli = item as IAccountMappingTemplateGroupItemFLI;

                // Process accounts directly in the FLI
                fli.accounts.forEach((acc) => {
                    if (isAccount(acc)) {
                        const account = acc as IAccount;
                        account.openingBalance = 0;
                        account.calculatedOpeningBalance = 0;
                        account.openingAdjustment = 0;
                        account.openingReClass = 0;
                        account.closingBalance = 0;
                        account.calculatedClosingBalance = 0;
                        account.closingAdjustment = 0;
                        account.closingReClass = 0;
                    } else if (isSubfli(acc)) {
                        // Process accounts inside SubFLIs
                        (acc as IAccountMappingSubFLI).accounts.forEach((subAcc) => {
                            subAcc.openingBalance = 0;
                            subAcc.calculatedOpeningBalance = 0;
                            subAcc.openingAdjustment = 0;
                            subAcc.openingReClass = 0;
                            subAcc.closingBalance = 0;
                            subAcc.calculatedClosingBalance = 0;
                            subAcc.closingAdjustment = 0;
                            subAcc.closingReClass = 0;
                        });
                    }
                });
            } else if (isGroup(item as SortedMappingItem)) {
                // Recursive call for nested groups
                this.resetTemplateBalances(item as IAccountMappingTemplateGroup);
            }
        }
    }

    get getImportedMapping(): IAccountMappingMap | undefined {
        return this.templateMapping['accountAssignments'] ?? this.fallbackImportedPlan?.['accountAssignments'];
    }

    /**
     * REV-22122: the previous year's imported plan, used by the automatic mapping only while the
     * template carries no plan of its own (a follow-up year without a confirmed taxonomy). Kept OUT of
     * `templateMapping` on purpose — the template must stay the saved one. Format and item sides are
     * read from the same source as the plan, never mixed.
     */
    private fallbackImportedPlan: IAccountMappingTemplate | undefined;

    setFallbackImportedPlan(plan: IAccountMappingTemplate | undefined): void {
        this.fallbackImportedPlan = plan;
    }

    get getFallbackImportedPlan(): IAccountMappingTemplate | undefined {
        return this.fallbackImportedPlan;
    }

    /** The source of the plan `getImportedMapping` returns: the template's own, else the fallback. */
    private get importedPlanSource(): IAccountMappingTemplate | undefined {
        return this.templateMapping['accountAssignments'] != null ? this.templateMapping : this.fallbackImportedPlan;
    }

    // REV-18627 (C1b, B2): reicht das Zuordnungsformat der importierten Vorlage durch, damit die
    // Zuordnungskaskade (mapWithImportedPlan) zwischen Kontonummer- und Zweck-Format entscheiden
    // kann.
    get getImportedMappingFormat(): 'accountNumber' | 'purpose' | undefined {
        return this.importedPlanSource?.['accountAssignmentsFormat'];
    }

    // REV-22122: die DATEV-Saldoart je Posten der importierten Vorlage (`accountItemSides`,
    // datev.excel.helper.ts) — reist wie das Format mit, damit die Zuordnungskaskade Saldierungsgruppen
    // in der Erfolgsrechnung der richtigen Seite zuordnen kann. Nicht im Typenpaket, darum per Index.
    get getImportedItemSides(): Record<string, string> | undefined {
        return (this.importedPlanSource as unknown as { accountItemSides?: Record<string, string> } | undefined)
            ?.accountItemSides;
    }

    get stateChangedSubject() {
        return this._stateChangedSubject.asObservable();
    }

    public get selectedAccounts(): IAccount[] {
        return this._selectedAccounts;
    }

    public get unSelectedAccounts(): IAccount[] {
        return this._unSelectedAccounts;
    }

    public get draggedAccounts(): IAccount[] {
        return this._draggedAccounts;
    }

    public get allFlis(): IAccountMappingTemplateGroupItemFLI[] {
        return this._allFlis;
    }

    public getUncoveredShortfallSpecifications(): IUncoveredShortfallSpecifications | undefined {
        return this.uncoveredShortfallSpecifications;
    }

    public getTemplateMapping(): IAccountMappingTemplate {
        return this.templateMapping;
    }

    static getMappingEngine(
        accounts: IAccount[],
        template: ITemplateEntityDTO,
        balanceSwitchAccounts: IBalanceSwitchAccount[],
        sheetSystemRules: IFinancialStatementInterpretation,
        dates: { startDate: string; endDate: string },
        mode: EngineModes,
        language: string,
        uncoveredShortfallSpecifications?: IUncoveredShortfallSpecifications,
        takePreparedAccountMappingTemplate?: IAccountMappingTemplate,
        previousYearTemplate?: IAccountMappingTemplate,
    ): AccountMappingEngine {
        let templateMapping = takePreparedAccountMappingTemplate;
        if (!takePreparedAccountMappingTemplate) {
            templateMapping = mapToAccountMappingTemplate(template, sheetSystemRules, language);
        }
        return new this(
            accounts,
            templateMapping,
            balanceSwitchAccounts,
            sheetSystemRules,
            dates,
            mode,
            undefined,
            uncoveredShortfallSpecifications,
            previousYearTemplate,
        );
    }

    private prepareDuplicateAccountSet(): void {
        const accountNumbers = this.accounts.filter((account) => account != null)
            .map((account) => account.accountNumber);

        const allDuplicateAccountNumber = accountNumbers.filter(
            (accountNumber, index) => accountNumbers.indexOf(accountNumber) !== index,
        );
        this.duplicateAccountNumberSet = new Set<string>(allDuplicateAccountNumber);
    }

    private breakPreviousYearDuplicateCurrentYearSingleAccounts(): void {
        const accountNumbers = this.accounts.filter((account) => account != null).map((account) => account.accountNumber);
        if (this.previousDuplicateAccountNumber == null) {
            return;
        }

        this.previousDuplicateAccountNumber.forEach((previousDuplicateAccountNumber) => {
            // account was duplicate in previous year but in current year it is not, should be broken
            // in case it is a balance switch account
            if (
                accountNumbers.includes(previousDuplicateAccountNumber) &&
                !this.duplicateAccountNumberSet.has(previousDuplicateAccountNumber)
            ) {
                const accountIndex = accountNumbers.indexOf(previousDuplicateAccountNumber);
                const account = this.accounts[accountIndex];
                if (account && account.isBalanceSwitchAccount) {
                    const firstBrokenPieceOfAccount: IAccount = {
                        ...account,
                        closingBalance: 0,
                    };
                    const secondBrokenPieceOfAccount: IAccount = {
                        ...account,
                        openingBalance: 0,
                    };

                    this.accounts[accountIndex] = firstBrokenPieceOfAccount;
                    // adding the second broken part of the account after the first to keep the order of accounts
                    this.accounts.splice(accountIndex + 1, 0, secondBrokenPieceOfAccount);
                }
            }
        });
    }
    public addOrUpdateAccount(
        oldAccount: IAccount | undefined,
        addedOrUpdatedAccount: IAccount,
        item?: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
        accountIndex?: number,
    ): void {
        const clonedAccount: IManualAddedOrUpdatedAccount = {
            ...addedOrUpdatedAccount,
            ignoreFliSign: true,
        };

        if (item != null && isOfThatItem(item) && addedOrUpdatedAccount.isMasterCloneAccount) {
            this.updateCloneAccountsOfMasterAccount(addedOrUpdatedAccount, item);
        }

        //update account in parent fli
        if (item != null && accountIndex != null) {
            item.accounts[accountIndex] = clonedAccount;
            item.accounts = this.sortAccounts(item.accounts);
        }

        if (oldAccount == null) {
            this.accounts.push(clonedAccount);
            return;
        }

        let updatedAccountIndex: number | undefined;

        // we identify an account by all row data incase it's a duplicate account number
        updatedAccountIndex = this.accounts.findIndex(
            (acc) =>
                acc.accountNumber === oldAccount.accountNumber &&
                acc.name === oldAccount.name &&
                acc.openingBalance === oldAccount.openingBalance &&
                acc.closingBalance === oldAccount.closingBalance,
        );

        if (updatedAccountIndex > -1) {
            this.accounts[updatedAccountIndex] = clonedAccount;
        } else {
            this.accounts.push(clonedAccount);
        }
    }
    public getType(): TemplateType {
        return this.templateMapping.type;
    }

    public getAllAccounts(): IAccount[] {
        return this.accounts;
    }

    public getBalanceSwitchAccounts(): IBalanceSwitchAccount[] {
        return this.balanceSwitchAccounts;
    }
    public getAddedAccountRowNumber(): number {
        const rowNo = Math.max(...this.accounts.map((account) => account.rowNo));
        return rowNo + 1;
    }

    public getMappedTemplate(): IAccountMappingTemplate {
        return this.templateMapping;
    }

    public deleteAccounts(items: IAccount[]): void {
        // emptying draggedAccounts so that they would not be mapped upon clicking on an fli
        this._draggedAccounts = [];
        this.accounts = differenceWith<IAccount, IAccount>(this.accounts, items, this.areAccountsEqual);
        this.calculateAll(true);
    }

    private _createAccountsDictionary(): void {
        this.accountsDictionaryWithAccountNumberAsKey = {};
        this.accountsDictionaryWithAccountDataAsKey = {};
        this.accountsDictionaryWithAccountDataWithoutBalanceAsKey = {};
        this.accountsDictionaryWithAccountDataWithOpeningBalanceAsKey = {};
        this.accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKey = {};
        const accountsDictionaryWithAccountDataWithoutBalanceAsKeyWithMoreThanOneAccount = new Set<string>();
        const accountsDictionaryWithAccountDataWithOpeningBalanceAsKeyWithMoreThanOneAccount = new Set<string>();
        const accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKeyWithMoreThanOneAccount = new Set<string>();
        for (const account of this.accounts) {
            if (account == null) {
                continue;
            }

            let closingAdjustment: number | undefined;
            if (account.closingAdjustment != null && NumberUtils.isFinite(account.closingAdjustment)) {
                closingAdjustment = NumberUtils.roundNumber(account.closingAdjustment);
            }

            let openingAdjustment: number | undefined;
            if (account.openingAdjustment != null && NumberUtils.isFinite(account.openingAdjustment)) {
                openingAdjustment = NumberUtils.roundNumber(account.openingAdjustment);
            }

            let closingReClass: number | undefined;
            if (account.closingReClass != null && NumberUtils.isFinite(account.closingReClass)) {
                closingReClass = NumberUtils.roundNumber(account.closingReClass);
            }

            let openingReClass: number | undefined;
            if (account.openingReClass != null && NumberUtils.isFinite(account.openingReClass)) {
                openingReClass = NumberUtils.roundNumber(account.openingReClass);
            }

            let calculatedClosingBalance: number | undefined;
            if (account.calculatedClosingBalance != null && NumberUtils.isFinite(account.calculatedClosingBalance)) {
                calculatedClosingBalance = NumberUtils.roundNumber(account.calculatedClosingBalance);
            }

            let calculatedOpeningBalance: number | undefined;
            if (account.calculatedOpeningBalance != null && NumberUtils.isFinite(account.calculatedOpeningBalance)) {
                calculatedOpeningBalance = NumberUtils.roundNumber(account.calculatedOpeningBalance);
            }

            let openingBalance = 0;
            if (account.openingBalance != null && NumberUtils.isFinite(account.openingBalance)) {
                openingBalance = NumberUtils.roundNumber(account.openingBalance);
            }

            let closingBalance = 0;
            if (account.closingBalance != null && NumberUtils.isFinite(account.closingBalance)) {
                closingBalance = NumberUtils.roundNumber(account.closingBalance);
            }
            const currentAccount: IAccount = {
                ...account,
                openingBalance,
                closingBalance,
                calculatedClosingBalance,
                calculatedOpeningBalance,
                closingAdjustment,
                openingAdjustment,
                closingReClass,
                openingReClass,
            };
            this.accountsDictionaryWithAccountNumberAsKey[account.accountNumber] = currentAccount;
            const accountDistinguishableIdentification = this.getDistinguishableAccountData(currentAccount);
            this.accountsDictionaryWithAccountDataAsKey[accountDistinguishableIdentification] = currentAccount;

            // Compare only names and account numbers (without balances)
            const accountDistinguishableIdentificationWithoutBalance = this.getDistinguishableAccountDataWithoutBalance(currentAccount);
            if (this.accountsDictionaryWithAccountDataWithoutBalanceAsKey[accountDistinguishableIdentificationWithoutBalance] != null) {
                // That means, there are two accounts with same data except balances
                accountsDictionaryWithAccountDataWithoutBalanceAsKeyWithMoreThanOneAccount.add(accountDistinguishableIdentificationWithoutBalance);
            }
            this.accountsDictionaryWithAccountDataWithoutBalanceAsKey[accountDistinguishableIdentificationWithoutBalance] = currentAccount;

            // Compare also opening balance
            const accountDistinguishableIdentificationWithOpeningBalance = this.getDistinguishableAccountDataWithBalance(currentAccount);
            if (this.accountsDictionaryWithAccountDataWithOpeningBalanceAsKey[accountDistinguishableIdentificationWithOpeningBalance] != null) {
                // That means, there are two accounts with same data except balances
                accountsDictionaryWithAccountDataWithOpeningBalanceAsKeyWithMoreThanOneAccount.add(accountDistinguishableIdentificationWithOpeningBalance);
            }
            this.accountsDictionaryWithAccountDataWithOpeningBalanceAsKey[accountDistinguishableIdentificationWithOpeningBalance] = currentAccount;

            // Compare also inverted opening balance
            const accountDistinguishableIdentificationWithInvertedOpeningBalance = this.getDistinguishableAccountDataWithInvertedBalance(currentAccount);
            if (this.accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKey[accountDistinguishableIdentificationWithInvertedOpeningBalance] != null) {
                // That means, there are two accounts with same data except balances
                accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKeyWithMoreThanOneAccount.add(accountDistinguishableIdentificationWithInvertedOpeningBalance);
            }
            this.accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKey[accountDistinguishableIdentificationWithInvertedOpeningBalance] = currentAccount;

        }
        // Remove entries from accountsDictionaryWithAccountDataWithoutBalanceAsKey which have more than one account
        accountsDictionaryWithAccountDataWithoutBalanceAsKeyWithMoreThanOneAccount.forEach((key) => {
            delete this.accountsDictionaryWithAccountDataWithoutBalanceAsKey[key];
        });
        // Remove entries from accountsDictionaryWithAccountDataWithOpeningBalanceAsKey which have more than one account
        accountsDictionaryWithAccountDataWithOpeningBalanceAsKeyWithMoreThanOneAccount.forEach((key) => {
            delete this.accountsDictionaryWithAccountDataWithOpeningBalanceAsKey[key];
        });
        // Remove entries from accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKey which have more than one account
        accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKeyWithMoreThanOneAccount.forEach((key) => {
            delete this.accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKey[key];
        });
    }

    public renameItem(item: IAccountMappingSubFLI, newCaption: string): void {
        item.caption = newCaption;
    }

    public renameGroup(group: IAccountMappingTemplateGroup, newTitle: string): void {
        group.title = newTitle;
        group.totalName = newTitle;
    }

    public updateFli(
        updatedFli: IAccountMappingTemplateGroupItemFLI,
        oldFli: IAccountMappingTemplateGroupItemFLI,
        newParentGroup: IAccountMappingTemplateGroup,
        oldParentGroup: IAccountMappingTemplateGroup,
    ): void {
        if (newParentGroup.name === oldParentGroup.name) {
            let fliIndex = oldParentGroup.items.findIndex((item) => isFli(item) && item.name === oldFli.name);

            let fliIndexInSortedItems = oldParentGroup.sortedItems.findIndex(
                (item) => isFli(item) && item.name === oldFli.name,
            );

            const fli = {
                ...oldFli,
                caption: updatedFli.caption,
                cashFlowType: updatedFli.cashFlowType,
                fliType: updatedFli.fliType,
            };

            oldParentGroup.items[fliIndex] = fli;
            oldParentGroup.sortedItems[fliIndexInSortedItems] = fli;

            return;
        }

        let oldFliIndex = oldParentGroup.items.findIndex((item) => isFli(item) && item.name === oldFli.name);
        let oldFliIndexInSortedItems = oldParentGroup.sortedItems.findIndex(
            (item) => isFli(item) && item.name === oldFli.name,
        );

        oldParentGroup.items.splice(oldFliIndex, 1);
        oldParentGroup.sortedItems.splice(oldFliIndexInSortedItems, 1);

        newParentGroup.items.push(updatedFli);
        newParentGroup.sortedItems.push(updatedFli);
    }

    public getTopGroup(): IAccountMappingTemplateGroup {
        return this.templateMapping.group;
    }

    public getTopGroups(): (IAccountMappingTemplateGroup | undefined)[] {
        if (this.templateMapping.type === TemplateType.BALANCE) {
            return this.getTopGroup().sortedItems as IAccountMappingTemplateGroup[];
        } else {
            return [this.getTopGroup()];
        }
    }

    public getAllFlis(group: IAccountMappingTemplateGroup): IAccountMappingTemplateGroupItemFLI[] {
        const flis: IAccountMappingTemplateGroupItemFLI[] = [];
        for (const item of group.items) {
            if (isFli(item)) {
                flis.push(item);
            }
        }
        for (const innerGroup of group.groups) {
            flis.push(...this.getAllFlis(innerGroup));
        }
        return flis;
    }

    public getGroup(name: DefaultGroupsNames | string): IAccountMappingTemplateGroup {
        return this.templateMapping.group.groups.find((gp) => gp.name === name);
    }

    public setDraggedAccountsInIndices(accounts: IAccount[]): void {
        this._draggedAccounts = [...accounts];
    }

    public putDraggedAccountsIntoFLI(item: IAccountMappingTemplateGroupItemFLI): void {

        this._putDraggedAccountsIntoFLI(item, this.draggedAccounts);
        this._draggedAccounts = [];
    }

    public putDraggedAccountsIntoSubFLI(
        parentFli: IAccountMappingTemplateGroupItemFLI,
        item: IAccountMappingSubFLI,
    ): void {
        item.accounts = this.getDraggedAccountsIntoSubFLI(parentFli, item, this.draggedAccounts);
        this._draggedAccounts = [];
    }

    private _putDraggedAccountsIntoFLI(item: IAccountMappingTemplateGroupItemFLI, accounts: IAccount[]): void {
        // In case the map income total to balance profit fli is clicked and the transferred profit account is created it should be removed if the user mapped another account to this fli
        if (
            item.accounts.length !== 0 &&
            item.accounts[IncomeTotalsAccountIndex].type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING
        ) {
            this.removeAccountFromFLI(item, IncomeTotalsAccountIndex);
        }

        const duplicateAccountNumbersInsideFli = item.accounts
            .filter((account1: IAccount) =>
                accounts.some((account2) => account1.accountNumber === account2.accountNumber),
            )
            .map((account: IAccount) => account.accountNumber);

        if (duplicateAccountNumbersInsideFli.length === EMPTY_ARRAY_LENGTH) {
            item.accounts = [
                ...item.accounts,
                ...accounts.map((account) =>
                    AccountMappingEngineHelper.getCorrectSign(item, account, this.sheetSystemRules),
                ),
            ];

            item.accounts = this.sortAccounts(item.accounts);

            return;
        }

        const accountsToBeAddedInsideFli = this.mergeDuplicateAccounts(
            duplicateAccountNumbersInsideFli,
            item,
            accounts,
        );
        item.accounts = accountsToBeAddedInsideFli;

        item.accounts = this.sortAccounts(item.accounts);
    }

    private mergeDuplicateAccounts(
        duplicateAccountNumbersInsideFli: string[],
        item: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        accounts: IAccount[],
        parentFli?: IAccountMappingTemplateGroupItemFLI,
    ): AccountMappingAccountItem[] {
        // Merge unmapped accounts (parameter accounts) with their duplicate accounts inside the selected fli into a single account

        const updatedAccounts: AccountMappingAccountItem[] = cloneDeep<AccountMappingAccountItem[]>(item.accounts);
        for (const account of accounts) {
            if (account == null) {
                continue;
            }
            // If unmapped account has no duplicate inside fli, then no merge is needed
            if (!duplicateAccountNumbersInsideFli.includes(account.accountNumber)) {
                updatedAccounts.push(account);
                continue;
            }

            if (
                this.previousDuplicateAccountNumber == null ||
                !this.previousDuplicateAccountNumber.has(account.accountNumber)
            ) {
                // If unmapped account was not a duplicate account in previous year, then no merge is needed
                updatedAccounts.push(account);
                continue;
            }

            const duplicateAccountInsideFli = item.accounts.find(
                (acc) => isAccount(acc) && acc.accountNumber === account.accountNumber,
            );

            // Apply signs on unmapped account before merge
            const accountToBeMergedWithCorrectSigns = AccountMappingEngineHelper.getCorrectSign(
                isSubfli(item) && parentFli != null ? parentFli : (item as IAccountMappingTemplateGroupItemFLI),
                account,
                this.sheetSystemRules,
            );

            if (
                (duplicateAccountInsideFli as IAccount).closingBalance !== 0 &&
                accountToBeMergedWithCorrectSigns.openingBalance !== 0
            ) {
                // only merge accounts where the mapped account has zero in closing and the unmapped account has zero in opening
                updatedAccounts.push(account);
                continue;
            }

            // Merge the 2 accounts into 1 single account where closing = unmapped account closing value , opening = duplicate account inside fli opening value
            const mergedAccount: IAccount = cloneDeep<IAccount>({
                ...account,
                closingBalance: accountToBeMergedWithCorrectSigns.closingBalance,
                openingBalance: (duplicateAccountInsideFli as IAccount).openingBalance,
            });

            // Replace mapped account inside fli with new single merged accounts
            const accountIndexInUpdatedAccountsArray = updatedAccounts.findIndex(
                (acc) =>
                    isAccount(acc) &&
                    acc.accountNumber == (duplicateAccountInsideFli as IAccount).accountNumber &&
                    acc.openingBalance === (duplicateAccountInsideFli as IAccount).openingBalance,
            );

            updatedAccounts[accountIndexInUpdatedAccountsArray] = mergedAccount;

            // Remove unmapped account from accounts array since it is now merged to an already mapped account
            const unmappedAccountIndexInAccountsArray = this.accounts.findIndex(
                (acc) =>
                    acc.accountNumber === accountToBeMergedWithCorrectSigns.accountNumber &&
                    acc.closingBalance === accountToBeMergedWithCorrectSigns.closingBalance,
            );

            if (unmappedAccountIndexInAccountsArray == -1) {
                throw new Error('unmappedAccountIndexInAccountsArray is undefined');
            }

            this.accounts.splice(unmappedAccountIndexInAccountsArray, 1);

            // Update mapped account after merge in accounts array
            const mappedAccountIndexInAccountsArray = this.accounts.findIndex(
                (acc) =>
                    acc.accountNumber === (duplicateAccountInsideFli as IAccount).accountNumber &&
                    acc.openingBalance === (duplicateAccountInsideFli as IAccount).openingBalance,
            );

            if (mappedAccountIndexInAccountsArray == -1) {
                throw new Error('mappedAccountIndexInAccountsArray is undefined');
            }

            this.accounts[mappedAccountIndexInAccountsArray] = mergedAccount;
        }
        return updatedAccounts;
    }

    public updateFliInItems(
        parentGroup: IAccountMappingTemplateGroup,
        updatedFli: IAccountMappingTemplateGroupItemFLI,
    ): void {
        const fliIndex = parentGroup.items.findIndex((item) => isFli(item) && item.name === updatedFli.name);
        parentGroup.items[fliIndex] = updatedFli;
    }

    private getDraggedAccountsIntoSubFLI(
        parentFli: IAccountMappingTemplateGroupItemFLI,
        item: IAccountMappingSubFLI,
        accounts: IAccount[],
    ): IAccount[] {
        const draggedAccounts = cloneDeep<IAccount[]>(accounts);

        const duplicateAccountNumbersInsideSubfli = item.accounts
            .filter((account1: IAccount) =>
                accounts.some((account2) => account1.accountNumber === account2.accountNumber),
            )
            .map((account: IAccount) => account.accountNumber);

        if (duplicateAccountNumbersInsideSubfli.length === EMPTY_ARRAY_LENGTH) {
            draggedAccounts.map((account) =>
                AccountMappingEngineHelper.getCorrectSign(parentFli, account, this.sheetSystemRules),
            );
            let result = item.accounts.concat(draggedAccounts);
            result = this.sortAccounts(result);
            return result as IAccount[];
        }

        const accountsToBeAddedInsideFli = this.mergeDuplicateAccounts(
            duplicateAccountNumbersInsideSubfli,
            item,
            accounts,
            parentFli,
        );

        let result = accountsToBeAddedInsideFli;
        result = this.sortAccounts(result);
        return result as IAccount[];
    }

    private getFlipSigns(fliType: FinancialLineItemType): boolean {
        return getFlipRule(defaultSystemRules, this.sheetSystemRules, fliType);
    }

    public removeAllAccountsFromFli(item: IAccountMappingTemplateGroupItemFLI, accountTopGroupName: string): void {
        item.accounts.forEach(() => {
            this.removeAccountFromFLI(item, item.accounts.length - 1, accountTopGroupName);
        });
    }

    public removeAllAccountsFromSubFli(item: IAccountMappingSubFLI, accountTopGroupName?: string): void {
        item.accounts.forEach(() => {
            this.removeAccountFromSubFLI(item, item.accounts.length - 1, accountTopGroupName);
        });
    }

    public removeAccountFromFLI(
        item: IAccountMappingTemplateGroupItemFLI,
        index: number,
        accountTopGroupName?: string,
    ): void {
        const itemToRemove = item.accounts[index];
        if (isSubfli(itemToRemove)) {
            this.removeAllAccountsFromSubFli(itemToRemove, accountTopGroupName);

            // remove subfli
            item.accounts = item.accounts.filter((_, indexOfItem) => indexOfItem !== index);

            return;
        }

        if (itemToRemove.type === AccountType.UNCOVERED_SHORTFALL) {
            this.removeUncoveredShortfallAccounts();
            return;
        }

        const isBalanceSwitchAccount = (item.accounts[index] as IAccount).isBalanceSwitchAccount;

        if (!isBalanceSwitchAccount) {
            //remove normally
            item.accounts = item.accounts.filter((_, indexOfItem) => indexOfItem !== index);
            return;
        }

        // remove 2 split duplicate accounts
        this.removeBalanceSwitchAccountFromFLIOrSubFLI(item, index, accountTopGroupName);
    }

    public removeAccountFromSubFLI(item: IAccountMappingSubFLI, index: number, accountTopGroupName?: string): void {
        const isBalanceSwitchAccount = (item.accounts[index] as IAccount).isBalanceSwitchAccount;

        if (!isBalanceSwitchAccount) {
            const accountToRemove = item.accounts[index];

            if (accountToRemove.isMasterCloneAccount) {
                this.removeCloneAccountsOfMasterAccount(accountToRemove, item);

                // revert isMasterCloneAccount boolean;
                this.addOrUpdateAccount(
                    accountToRemove,
                    { ...accountToRemove, isMasterCloneAccount: false },
                    item,
                    index,
                );

                this.masterCloneAccountsToNumberOfCloneAccountsMap.delete(accountToRemove);
            }

            if (accountToRemove.isCloneAccount) {
                const allMasterAccounts = this.masterCloneAccountsToNumberOfCloneAccountsMap.keys();
                const masterCloneAccount = Array.from(allMasterAccounts)
                    .filter((acc) => acc != null)
                    .find((acc) => acc.accountNumber === accountToRemove.accountNumber);
                // subtract 1 from clone Accounts
                const numberOfCloneAccounts =
                    this.masterCloneAccountsToNumberOfCloneAccountsMap.get(masterCloneAccount) - 1;
                this.masterCloneAccountsToNumberOfCloneAccountsMap.set(masterCloneAccount, numberOfCloneAccounts);

                if (numberOfCloneAccounts === 0) {
                    // if no more clone accounts exists for the master account, then we need to revert the master account booleam
                    const ofThatItemSiblings = this.getOfThatItemSiblings(item);
                    for (const ofThatItemSibling of ofThatItemSiblings) {
                        const masterAccountIndexInsideofThatItem = ofThatItemSibling.accounts.findIndex(
                            (acc) => acc.accountNumber === accountToRemove.accountNumber && acc.isMasterCloneAccount,
                        );

                        if (masterAccountIndexInsideofThatItem > -1) {
                            this.addOrUpdateAccount(
                                ofThatItemSibling.accounts[masterAccountIndexInsideofThatItem],
                                {
                                    ...ofThatItemSibling.accounts[masterAccountIndexInsideofThatItem],
                                    isMasterCloneAccount: false,
                                },
                                ofThatItemSibling,
                                masterAccountIndexInsideofThatItem,
                            );
                        }
                    }

                    // remove master account entry from map;
                    this.masterCloneAccountsToNumberOfCloneAccountsMap.delete(masterCloneAccount);
                }

                // remove clone account from accounts array as well
                const accountIndexInAccountsArray = this.accounts.findIndex(
                    (acc) => acc.accountNumber === accountToRemove.accountNumber && acc.isCloneAccount,
                );
                this.accounts.splice(accountIndexInAccountsArray, 1);
            }

            item.accounts = item.accounts.filter((_, indexOfItem) => indexOfItem !== index);

            return;
        }

        this.removeBalanceSwitchAccountFromFLIOrSubFLI(item, index, accountTopGroupName);
    }

    public addSubFli(parentFli: IAccountMappingTemplateGroupItemFLI, item: IAccountMappingSubFLI): void {
        if (
            parentFli.accounts.length > 0 &&
            parentFli.accounts[IncomeTotalsAccountIndex].type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING
        ) {
            this.removeAccountFromFLI(parentFli, IncomeTotalsAccountIndex);
        }

        parentFli.accounts = [...parentFli.accounts, item];
        parentFli.accounts = this.sortAccounts(parentFli.accounts);
    }

    public addFli(parentGroup: IAccountMappingTemplateGroup, fli: IAccountMappingTemplateGroupItemFLI): void {
        parentGroup.items = [...parentGroup.items, fli];
        parentGroup.sortedItems = [...(parentGroup.sortedItems as SortedMappingItem[]), fli];
    }

    public addGroup(parentGroup: IAccountMappingTemplateGroup, group: IAccountMappingTemplateGroup): void {
        parentGroup.groups = [...parentGroup.groups, group];
        parentGroup.sortedItems = [...(parentGroup.sortedItems as SortedMappingItem[]), group];
    }

    public addSubtotal(
        parentGroup: IAccountMappingTemplateGroup,
        subtotal: IAccountMappingTemplateGroupItemSubtotal,
    ): void {
        parentGroup.items = [...parentGroup.items, subtotal];
        parentGroup.sortedItems = [...(parentGroup.sortedItems as SortedMappingItem[]), subtotal];
    }

    public addSubtotalAfterIndex(
        parentGroup: IAccountMappingTemplateGroup,
        subtotal: IAccountMappingTemplateGroupItemSubtotal,
        index: number,
        placeSubtotalAsFirstItem: boolean,
    ): void {
        // Ensure the index is within bounds
        if ((!placeSubtotalAsFirstItem && index < 0) || index >= parentGroup.sortedItems.length) {
            throw new Error('Invalid index provided for insertion.');
        }
        // Insert the subtotal after the specified index for both items and sortedItems
        parentGroup.items = [...parentGroup.items, subtotal];

        if (placeSubtotalAsFirstItem) {
            parentGroup.sortedItems.unshift(subtotal);
        } else {
            parentGroup.sortedItems.splice(index + 1, 0, subtotal);
        }
    }

    public updateSubtotal(
        updatedSubtotal: IAccountMappingTemplateGroupItemSubtotal,
        oldSubtotal: IAccountMappingTemplateGroupItemSubtotal,
        newSubtotalIndexInSortedItems: number,
        oldSubtotalIndexInSortedItems: number,
        parentGroup: IAccountMappingTemplateGroup,
    ): void {
        let oldSubtotalInItemsIndex = parentGroup.items.findIndex(
            (item) => isSubTotal(item) && item.name === oldSubtotal.name,
        );

        parentGroup.items.splice(oldSubtotalInItemsIndex, 1);
        parentGroup.sortedItems.splice(oldSubtotalIndexInSortedItems, 1);

        parentGroup.items.push(updatedSubtotal);
        if (newSubtotalIndexInSortedItems <= oldSubtotalIndexInSortedItems) {
            parentGroup.sortedItems.splice(newSubtotalIndexInSortedItems + 1, 0, updatedSubtotal);
        } else {
            parentGroup.sortedItems.splice(newSubtotalIndexInSortedItems, 0, updatedSubtotal);
        }
    }

    /**
     * REV-20899: provide the transactional reclassifications + statement for
     * this engine. Call after construction (or whenever the transactions
     * change). Has no effect for version 0 (legacy AR not yet migrated).
     */
    public setReclassifications(
        transactions: IReclassificationTransaction[] | undefined | null,
        statement: ReclassificationStatement | undefined,
        version: 0 | 1 | undefined,
    ): void {
        this.reclassTransactions = Array.isArray(transactions) ? transactions : [];
        this.reclassStatement = statement;
        this.reclassVersion = version;
    }

    /**
     * Walk every account-like node in the template (real accounts in FLIs,
     * accounts in subFLIs, and IReclassesAccounts phantoms). Used to zero
     * derived reclass fields and to apply transactions by composite key.
     */
    private forEachTemplateAccount(
        group: IAccountMappingTemplateGroup,
        visitor: (acc: IAccount | IReclassesAccounts) => void,
    ): void {
        if (group == null) return;
        const walkFli = (fli: IAccountMappingTemplateGroupItemFLI): void => {
            if (Array.isArray(fli.accounts)) {
                for (const a of fli.accounts) {
                    if (a == null) continue;
                    if (isSubfli(a)) {
                        const sub = a as IAccountMappingSubFLI;
                        if (Array.isArray(sub.accounts)) {
                            for (const sa of sub.accounts) {
                                if (sa) visitor(sa);
                            }
                        }
                        if (Array.isArray(sub.reclassesAccounts)) {
                            for (const ra of sub.reclassesAccounts) {
                                if (ra) visitor(ra);
                            }
                        }
                    } else if (!isEmpty(a) && !isHeader(a)) {
                        visitor(a as IAccount);
                    }
                }
            }
            if (Array.isArray(fli.reclassesAccounts)) {
                for (const ra of fli.reclassesAccounts) {
                    if (ra) visitor(ra);
                }
            }
        };
        const walkGroup = (g: IAccountMappingTemplateGroup): void => {
            // The view models populate `sortedItems` (which is what the table
            // renders from and what `calculateGroup` iterates). Fall back to
            // `items` if `sortedItems` isn't set yet.
            const itemList: unknown[] | null =
                (Array.isArray(g.sortedItems) ? (g.sortedItems as unknown[]) : null) ??
                (Array.isArray(g.items) ? (g.items as unknown[]) : null);
            if (itemList) {
                for (const item of itemList) {
                    if (item != null && isFli(item as SortedMappingItem)) {
                        walkFli(item as IAccountMappingTemplateGroupItemFLI);
                    }
                }
            }
            if (Array.isArray(g.groups)) {
                for (const sub of g.groups) walkGroup(sub);
            }
        };
        walkGroup(group);
    }

    /**
     * REV-20899: zero every account's reclass fields and re-derive them from
     * the configured transactions. Only runs when `reclassVersion === 1`.
     */
    private applyReclassificationsFromTransactions(): void {
        if (this.reclassVersion !== 1) return;
        if (this.templateMapping == null || this.templateMapping.group == null) return;

        // Collect every account-like node for composite-key resolution.
        const accountIndex: Array<IAccount | IReclassesAccounts> = [];
        this.forEachTemplateAccount(this.templateMapping.group, (acc) => {
            // Zero the legacy fields; they become derived from transactions.
            (acc as IReclassesAndAdjustmentExtension).openingReClass = 0;
            (acc as IReclassesAndAdjustmentExtension).closingReClass = 0;
            accountIndex.push(acc);
        });

        if (this.reclassTransactions.length === 0) return;
        const statement = this.reclassStatement;

        const resolve = (ref: IReclassificationAccountRef): IAccount | IReclassesAccounts | undefined => {
            // Exact composite-key match first.
            const exact = accountIndex.filter(
                (a) => a.accountNumber === ref.accountNumber && (a.name ?? '') === ref.name,
            );
            if (exact.length === 1) return exact[0];
            if (exact.length > 1) return undefined;
            // Fallback: unique-by-number (legacy compat for renamed accounts).
            const byNumber = accountIndex.filter((a) => a.accountNumber === ref.accountNumber);
            if (byNumber.length === 1) return byNumber[0];
            return undefined;
        };

        for (const t of this.reclassTransactions) {
            if (t == null) continue;
            if (statement != null && t.statement !== statement) continue;
            const field: keyof IReclassesAndAdjustmentExtension =
                t.period === ReclassificationPeriod.CURRENT_YEAR ? 'closingReClass' : 'openingReClass';
            const src = resolve(t.source);
            const tgt = resolve(t.target);
            if (src) {
                (src as IReclassesAndAdjustmentExtension)[field] =
                    ((src as IReclassesAndAdjustmentExtension)[field] ?? 0) - t.amount;
            }
            if (tgt) {
                (tgt as IReclassesAndAdjustmentExtension)[field] =
                    ((tgt as IReclassesAndAdjustmentExtension)[field] ?? 0) + t.amount;
            }
            // Unresolved refs are surfaced as conflicts by the UI; their delta is discarded here.
        }
    }

    public calculateAll(rebase: boolean = false): void {
        this.accounts = this.accounts.filter((account) => account != null);

        if (rebase) {
            this._createAccountsDictionary();
        }
        this._allFlis = [];
        this._selectedAccounts = [];

        this.applyReclassificationsFromTransactions();

        this.calculateGroup(this.templateMapping.group, new Map(), rebase);

        this._unSelectedAccounts = differenceWith<IAccount, IAccount>(
            this.accounts,
            this._selectedAccounts,
            this.areAccountsEqual,
        ).filter((account) => account.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING);
        this.resetEngineMode();
        this._stateChangedSubject.next({});
    }

    private resetEngineMode(): void {
        this.mode = EngineModes.MANUAL;
    }

    // checking the equality is made manually as those are the properties which identify if accounts are equal or not
    private areAccountsEqual(firstAccount: IAccount | undefined, secondAccount: IAccount | undefined): boolean {
        if (firstAccount == null || secondAccount == null) {
            return false;
        }
        return (
            firstAccount.accountNumber === secondAccount.accountNumber &&
            firstAccount.name === secondAccount.name &&
            firstAccount.shareholder === secondAccount.shareholder &&
            firstAccount.openingBalance === secondAccount.openingBalance &&
            firstAccount.closingBalance === secondAccount.closingBalance
        );
    }

    private calculateGroup(
        group: IAccountMappingTemplateGroup,
        parentSums: Map<string, ICalculation>,
        rebase: boolean = false,
    ): ICalculation {
        let sum: ICalculation = AccountMappingEngineHelper.getEmptySum();
        parentSums.set(group.name, sum);
        for (const sortedItem of group.sortedItems) {
            if (isGroup(sortedItem as SortedMappingItem)) {
                const iterationGroupSum = this.calculateGroup(
                    sortedItem as IAccountMappingTemplateGroup,
                    parentSums,
                    rebase,
                );

                sum = AccountMappingEngineHelper.addValueToSum(sum, iterationGroupSum);
            } else if (isSubTotal(sortedItem as SortedMappingItem)) {
                // Use the name property for the lookup

                const groupName = (sortedItem as IAccountMappingTemplateGroupItemSubtotal).scope.name;

                // Get the value from the map based on the groupName
                const parentSum = parentSums.get(groupName);
                AccountMappingEngineHelper.assignCalculationItems(
                    sortedItem as IAccountMappingTemplateGroupItemSubtotal,
                    parentSum,
                );
            } else if (isFli(sortedItem as SortedMappingItem)) {
                this._allFlis.push(sortedItem as IAccountMappingTemplateGroupItemFLI);
                const fliSum = this.calculateFLI(sortedItem as IAccountMappingTemplateGroupItemFLI, undefined, rebase);
                sum = AccountMappingEngineHelper.addValueToSum(sum, fliSum);
                parentSums.forEach((value, key) => {
                    const valueSum = AccountMappingEngineHelper.addValueToSum(value, fliSum);
                    parentSums.set(key, valueSum);
                });
            }
        }

        AccountMappingEngineHelper.assignCalculationItems(group, sum);
        parentSums.delete(group.name);
        return sum;
    }

    private initAdjustmentAndReclasses(val: IReclassesAndAdjustmentExtension): void {
        let closingAdjustment: number | undefined;
        if (val.closingAdjustment != null && NumberUtils.isFinite(val.closingAdjustment)) {
            closingAdjustment = NumberUtils.roundNumber(val.closingAdjustment);
        }

        let closingReClass: number | undefined;
        if (val.closingReClass != null && NumberUtils.isFinite(val.closingReClass)) {
            closingReClass = NumberUtils.roundNumber(val.closingReClass);
        }

        let openingAdjustment: number | undefined;
        if (val.openingAdjustment != null && NumberUtils.isFinite(val.openingAdjustment)) {
            openingAdjustment = NumberUtils.roundNumber(val.openingAdjustment);
        }

        let openingReClass: number | undefined;
        if (val.openingReClass != null && NumberUtils.isFinite(val.openingReClass)) {
            openingReClass = NumberUtils.roundNumber(val.openingReClass);
        }

        val.closingAdjustment = closingAdjustment;
        val.closingReClass = closingReClass;
        val.openingAdjustment = openingAdjustment;
        val.openingReClass = openingReClass;
    }

    private calculateFLI(
        fli: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        flipSign?: boolean,
        rebase: boolean = false,
    ): ICalculation {
        if (isFli(fli)) {
            flipSign = this.getFlipSigns(fli.fliType);
        }

        const sign = flipSign ? -1 : 1;
        let sum: ICalculation = AccountMappingEngineHelper.getEmptySum();

        // 'sumWithoutduplicate' only used inside subflis
        let sumWithoutduplicate = AccountMappingEngineHelper.getEmptySum();

        const accounts = [];
        const reclassesAccounts = [];

        for (let item of fli.accounts || []) {
            if (isSubfli(item)) {
                const val = this.calculateFLI(item, flipSign, rebase);
                sum = AccountMappingEngineHelper.addValueToSum(sum, val);

                accounts.push(item);
            } else if (!isEmpty(item) && !isHeader(item)) {
                // if item is an account
                if (rebase) {
                    const account = this.getAccountBasedOnType(item as IAccount, fli);
                    if (account == null) {
                        continue;
                    }
                    // FROM STAGING: Hotfix for re-calculation of balance switch accounts
                    let closingBalance = 0;
                    let calculatedClosingBalance = 0;
                    if (account["markForBalanceSwitchAccountFix"]) {
                        closingBalance = account["markForBalanceSwitchAccountFixClosingBalance"] ?? 0;
                        calculatedClosingBalance = Math.abs(closingBalance);
                        delete account["markForBalanceSwitchAccountFix"];
                    }
                    this.assignAdjustmentsAndReclassesToAccountIfNotPresent(account, item as IAccount);
                    item = account;
                }
                if (item != null) {
                    this._selectedAccounts.push(item as IAccount);
                    // this is to ignore the flip sign for the transferred profit from the income statement
                    this.useSignBasedOnAccountType(item as IAccount, sign);
                    this.initAdjustmentAndReclasses(item);

                    accounts.push(item);

                    const shouldSkipAccountFromSummation = (item as IAccount).isCloneAccount;

                    if (!shouldSkipAccountFromSummation && isOfThatItem(fli)) {
                        sumWithoutduplicate = AccountMappingEngineHelper.addValueToSum(sumWithoutduplicate, item);
                    }

                    // account item will have only intrinsic reclasses and adjustment values
                    // (no sign effect on the reclasses and the adjustment)
                    sum = AccountMappingEngineHelper.addValueToSum(sum, item);
                }
            } else if (isEmpty(item) || isHeader(item)) {
                accounts.push(item);
            }
        }

        for (const item of fli.reclassesAccounts || []) {
            if (item.type === AccountType.RECLASSES) {
                sum = AccountMappingEngineHelper.addValueToSum(sum, item);
                sumWithoutduplicate = AccountMappingEngineHelper.addValueToSum(sumWithoutduplicate, item);

                this.initAdjustmentAndReclasses(item);
                reclassesAccounts.push(item);
            }
        }
        fli.accounts = accounts;
        fli.reclassesAccounts = reclassesAccounts;

        // always assign 'sum' to self, since even returning sumWithoutduplicate incase of subfli, it is put in variable sum for parent fli.
        AccountMappingEngineHelper.assignCalculationItems(fli, sum);

        if (isOfThatItem(fli)) {
            // return to parent fli 'sumWithoutduplicate'
            return sumWithoutduplicate;
        }

        return sum;
    }

    //
    private getAccountBasedOnType(currentAccount: IAccount, fli: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,): IAccount | undefined {
        if (this.mode === EngineModes.MANUAL) {
            return this.getAccountsBasedOnTypeOfManualMode(currentAccount);
        } else if (this.mode === EngineModes.AUTOFILL) {
            return this.getAccountBasedOnTypeOfAutofillMode(currentAccount);
        } else if (
            this.mode === EngineModes.PREVIOUS_AUTOMATIC_FILL ||
            this.mode === EngineModes.COPY_FROM_OTHER_REPORT
        ) {
            return this.getAccountBasedOnTypeOfPreviousAutomaticFill(currentAccount, fli);
        }
        return undefined;
    }

    private getAccountsBasedOnTypeOfManualMode(currentAccount: IAccount): IAccount | undefined {
        let account: IAccount;

        // FROM STAGING: Hotfix - check if not duplicate in current year but IS balance switch account
        const isAccountDuplicateInCurrentYear = this.duplicateAccountNumberSet.has(currentAccount.accountNumber);
        if (!isAccountDuplicateInCurrentYear && currentAccount.isBalanceSwitchAccount) {
            // In case it's not duplicate in current year, but it's a balance switch account AND the balance switch account array is not existing yet
            // Then we should map it directly to both balanace switch accounts based on the balances

            // Assuming currentAccount is the one you are trying to match
            if (this.balanceSwitchAccounts == null || this.balanceSwitchAccounts.length === 0) {

                // 1. Direct lookup from our new Map
                const adjustmentEntry = this.balanceSwitchAdjustments.get(currentAccount.accountNumber);

                if (adjustmentEntry && adjustmentEntry.accountsToFix.length > 0) {
                    // Find the specific account in the 'ToFix' group
                    // Usually, there's only one relevant account here per side (Asset vs Liability)
                    const match = adjustmentEntry.accountsToFix.find(acc =>
                        acc.accountNumber === currentAccount.accountNumber
                    );
                    const accountWith0 = adjustmentEntry.accountsToZero.find(acc =>
                        acc.accountNumber === currentAccount.accountNumber
                    );

                    if (match) {
                        // 2. Perform your balance validation
                        const isBalanceMatch = Math.abs(match.openingBalance) === Math.abs(currentAccount.openingBalance);

                        if (isBalanceMatch) {
                            return match;
                        } else {
                            return accountWith0;
                        }
                    }
                }

                // 3. Fallback/Warning logic
                console.warn(
                    'Balance switch accounts mapping failed: could not find valid match.',
                    currentAccount.accountNumber,
                    currentAccount.name
                );
            }
        }

        if (
            currentAccount.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING &&
            !this.duplicateAccountNumberSet.has(currentAccount.accountNumber)
        ) {
            // map of normal account
            account = this.accountsDictionaryWithAccountNumberAsKey[currentAccount.accountNumber];
        } else if (
            currentAccount.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING &&
            this.duplicateAccountNumberSet.has(currentAccount.accountNumber)
        ) {
            // map of a duplicate account
            // TODO: maybe this is not correct because it can be possible that two accounts have same data except balances
            // In another case I had to remove this check because it mapped to wrong accounts
            account = this.accountsDictionaryWithAccountDataAsKey[this.getDistinguishableAccountData(currentAccount)];
            if (account == null) {
                // try to map without balance key
                account = this.accountsDictionaryWithAccountDataWithoutBalanceAsKey[this.getDistinguishableAccountDataWithoutBalance(currentAccount)];
            }
        } else if (currentAccount.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING) {
            // map of special account
            account = currentAccount;
        }
        return account;
    }

    private getAccountBasedOnTypeOfAutofillMode(account: IAccount): IAccount | undefined {
        if (account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING) {
            // special account is not mapped while autofill
            return undefined;
        }

        const isAccountDuplicateInPreviousYear =
            this.previousDuplicateAccountNumber != null &&
            this.previousDuplicateAccountNumber.has(account.accountNumber);

        const isShareHolderAccount = account.shareholder != null && account.shareholder !== ''
        /*if (isAccountDuplicateInPreviousYear && !isShareHolderAccount) {

            // TODO: here we can try to find a more granular match on duplicate accounts
            // --- TODO Duplicate account matching logic start ---
            // TODO
        }*/
        /*
        if (isAccountDuplicateInPreviousYear && isShareHolderAccount) {

            // TODO: here we can try to find a more granular match on duplicate accounts
            // --- TODO Duplicate account matching logic start ---
            // TODO
        }*/
        if (isAccountDuplicateInPreviousYear) {
            // 'account' input is the account mapped inside last year template with old values. Hence has last year values that doesn't match yet with the accountsDictionaryWithAccountDataAsKey.
            // Therefore, we need to get updated values for this account.
            const updatedMatchingAccount =
                this.getCorrespondingCurrentYearAccountOfPreviousYearAccountWithUpdatedValues(account);
            if (updatedMatchingAccount == null) {
                return undefined;
            }
            let accountToReturn = this.accountsDictionaryWithAccountDataAsKey[
                this.getDistinguishableAccountData(updatedMatchingAccount)
            ];
            if (accountToReturn == null) {
                // try to map without balance key
                accountToReturn = this.accountsDictionaryWithAccountDataWithoutBalanceAsKey[this.getDistinguishableAccountDataWithoutBalance(updatedMatchingAccount)];
            }
            if (accountToReturn == null) {
                // try to map with opening balance key
                accountToReturn = this.accountsDictionaryWithAccountDataWithOpeningBalanceAsKey[this.getDistinguishableAccountDataWithBalance(updatedMatchingAccount)];
            }
            if (accountToReturn == null) {
                // try to map with inverted opening balance key
                accountToReturn = this.accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKey[this.getDistinguishableAccountDataWithInvertedBalance(updatedMatchingAccount)];
            }
            return accountToReturn;
        }
        return this.accountsDictionaryWithAccountNumberAsKey[account.accountNumber];
    }

    private getAccountBasedOnTypeOfPreviousAutomaticFill(
        account: MappingEngineAccount,
        fli: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
    ): MappingEngineAccount | undefined {
        const isAccountDuplicateInPreviousYear =
            this.previousDuplicateAccountNumber != null &&
            this.previousDuplicateAccountNumber.has(account.accountNumber);

        const isAccountDuplicateInCurrentYear = this.duplicateAccountNumberSet.has(account.accountNumber);

        // For COPY_FROM_OTHER_REPORT mode: if an account was duplicated in the source report
        // We cannot merge based on the account balance.

        if (isAccountDuplicateInCurrentYear && account.isBalanceSwitchAccount) {
            let accountToReturn = this.accountsDictionaryWithAccountDataAsKey[this.getDistinguishableAccountData(account)];
            if (accountToReturn == null) {
                // try to map without balance key
                accountToReturn = this.accountsDictionaryWithAccountDataWithoutBalanceAsKey[this.getDistinguishableAccountDataWithoutBalance(account)];
            }
            if (accountToReturn == null) {
                // try to map with opening balance key
                accountToReturn = this.accountsDictionaryWithAccountDataWithOpeningBalanceAsKey[this.getDistinguishableAccountDataWithBalance(account, true)];
            }

            return accountToReturn;
        }

        // FROM STAGING: Hotfix - Handle Balance Switch logic when NOT duplicate in current year
        if (!isAccountDuplicateInCurrentYear && account.isBalanceSwitchAccount) {
            // In case it's not duplicate in current year, but it's a balance switch account AND the balance switch account array is not existing yet
            // Then we should map it directly to both balanace switch accounts based on the balances

            // Assuming account is the one you are trying to match
            if (this.balanceSwitchAccounts == null || this.balanceSwitchAccounts.length === 0) {

                // 1. Direct lookup from our new Map
                const adjustmentEntry = this.balanceSwitchAdjustments.get(account.accountNumber);

                if (adjustmentEntry && adjustmentEntry.accountsToFix.length > 0) {
                    // Find the specific account in the 'ToFix' group
                    // Usually, there's only one relevant account here per side (Asset vs Liability)
                    const match = adjustmentEntry.accountsToFix.find(acc =>
                        acc.accountNumber === account.accountNumber
                    );
                    const accountWith0 = adjustmentEntry.accountsToZero.find(acc =>
                        acc.accountNumber === account.accountNumber
                    );

                    if (match) {
                        // 2. Perform your balance validation
                        const isBalanceMatch = Math.abs(match.closingBalance) === Math.abs(account.closingBalance);

                        if (isBalanceMatch) {

                            const shiftedAccount: MappingEngineAccount = cloneDeep<MappingEngineAccount>({
                                ...match,
                                openingBalance: match.closingBalance,
                                calculatedOpeningBalance: match.calculatedClosingBalance,
                                closingBalance: match['markForBalanceSwitchAccountFixClosingBalance'] ?? 0,
                                calculatedClosingBalance: Math.abs(match['markForBalanceSwitchAccountFixClosingBalance'] ?? 0),
                                isShifted: true,
                            } as MappingEngineAccount);
                            return shiftedAccount;
                        } else {
                            return accountWith0;
                        }
                    }
                }

                // 3. Fallback/Warning logic
                console.warn(
                    'Balance switch accounts mapping failed: could not find valid match.',
                    account.accountNumber,
                    account.name
                );
            }

        }

        if (isAccountDuplicateInCurrentYear && !account.isBalanceSwitchAccount && account.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING) {

            // try to map without balance key
            let accountToReturn = this.accountsDictionaryWithAccountDataWithoutBalanceAsKey[this.getDistinguishableAccountDataWithoutBalance(account)];

            if (this.mode === EngineModes.COPY_FROM_OTHER_REPORT) {
                // For COPY_FROM_OTHER_REPORT mode: if an account was duplicated in the source report
                // We cannot merge based on the account balance.
                return accountToReturn;
            }

            if (accountToReturn == null) {
                // try to map with opening balance key
                accountToReturn = this.accountsDictionaryWithAccountDataWithOpeningBalanceAsKey[this.getDistinguishableAccountDataWithBalance(account, true)];
            }
            if (accountToReturn == null) {
                // try to map with inverted opening balance key
                accountToReturn = this.accountsDictionaryWithAccountDataWithInvertedOpeningBalanceAsKey[this.getDistinguishableAccountDataWithInvertedBalance(account, true)];
            }
            return accountToReturn;
        }

        // FROM DEV: Merged Accounts Map logic
        if (this.mergedAccountsMap != null && this.mergedAccountsMap.has(account.accountNumber)) {
            return this.mergedAccountsMap.get(account.accountNumber);
        }

        // FROM DEV: Summarized Accounts logic
        if (account.isSummarized) {
            // This is a summarized account from the previous year.
            // Find the corresponding summarized account in the current year's data.
            const currentYearSummarizedAccount = this.accounts.find(
                (acc) => acc.accountNumber === account.accountNumber && acc.isSummarized,
            );

            if (currentYearSummarizedAccount) {
                // Determine the correct sign for the previous year's value
                // Merge the previous year's value (closingBalance) into the current year's openingBalance.
                currentYearSummarizedAccount.openingBalance = account.closingBalance;
                // Apply the sign to the calculated balance.
                currentYearSummarizedAccount.calculatedOpeningBalance = account.calculatedClosingBalance;

                currentYearSummarizedAccount.comingFromPreviousAnnualReport = true;
                return currentYearSummarizedAccount;
            }
        }


        if (isAccountDuplicateInPreviousYear && account.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING) {
            if (account.isShifted) {
                return account;
            }
            // account input is that of last year, so we need to shift it's closing value to the opening and set closing with 0
            const shiftedAccount = this.shiftAccountClosingValueToOpeningValue(account);
            if (!this.accounts.includes(shiftedAccount)) {
                this.accounts.push(shiftedAccount);
            }
            return shiftedAccount;
        }

        // not a duplicate, autofill normally
        if (
            !isAccountDuplicateInPreviousYear &&
            !isAccountDuplicateInCurrentYear &&
            account.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING
        ) {
            return this.accountsDictionaryWithAccountNumberAsKey[account.accountNumber];
        } else if (account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING) {
            // the automatic previous year fill already done
            if (account.closingBalance === 0) return account;

            const specialAccount = { ...account };
            specialAccount.openingBalance = NumberUtils.roundNumber(account.closingBalance);
            specialAccount.calculatedOpeningBalance = NumberUtils.roundNumber(account.closingBalance);

            specialAccount.closingBalance = 0;
            specialAccount.calculatedClosingBalance = 0;

            return specialAccount;
        }
        return undefined;
    }

    private getCorrespondingCurrentYearAccountOfPreviousYearAccountWithUpdatedValues(
        previousYearAccount: IAccount,
    ): IAccount | undefined {
        const currentYearAccount = this.accounts.find(
            (acc) =>
                acc.accountNumber === previousYearAccount.accountNumber &&
                acc.name === previousYearAccount.name &&
                acc.openingBalance === previousYearAccount.closingBalance,
        );

        if (currentYearAccount == null) {
            // Incase input account is already the current updated account
            const matchingAccount = this.accounts.find(
                (acc) =>
                    acc.accountNumber === previousYearAccount.accountNumber &&
                    acc.name === previousYearAccount.name &&
                    acc.openingBalance === previousYearAccount.openingBalance &&
                    acc.closingBalance === previousYearAccount.closingBalance,
            );
            return matchingAccount;
        }

        return currentYearAccount;
    }

    private shiftAccountClosingValueToOpeningValue(account: MappingEngineAccount): MappingEngineAccount {
        const shiftedAccount: MappingEngineAccount = cloneDeep<MappingEngineAccount>({
            ...account,
            openingBalance: account.closingBalance,
            calculatedOpeningBalance: account.calculatedClosingBalance,
            closingBalance: 0,
            calculatedClosingBalance: 0,
            isShifted: true,
        } as MappingEngineAccount);

        this.addOrUpdateAccount(account, shiftedAccount);

        return shiftedAccount;
    }

    private getDistinguishableAccountData(account: IAccount): string {
        let distinguishableAccountData: string = `${account.accountNumber}-${account.name}`;

        distinguishableAccountData =
            distinguishableAccountData + `-${account.closingBalance}-${account.openingBalance}`;

        if (account.shareholder != null && account.shareholder !== '') {
            distinguishableAccountData = distinguishableAccountData + `-${account.shareholder}`;
        }

        if (account.isCloneAccount) {
            distinguishableAccountData = distinguishableAccountData + account.isCloneAccount;
        }

        return distinguishableAccountData;
    }

    private getDistinguishableAccountDataWithBalance(account: IAccount, fromPreviousYearAccount: boolean = false): string {
        let distinguishableAccountData: string = `${account.accountNumber}`;


        if (fromPreviousYearAccount) {
            distinguishableAccountData = account.calculatedClosingBalance != null
                ? distinguishableAccountData + `-${account.calculatedClosingBalance}`
                :
                distinguishableAccountData + `-${account.closingBalance}`;
            debug('distinguishableAccountData fromPreviousYearAccount', distinguishableAccountData, account.closingBalance, account.calculatedClosingBalance);

        } else {
            distinguishableAccountData = account.calculatedOpeningBalance != null
                ? distinguishableAccountData + `-${account.calculatedOpeningBalance}`
                :
                distinguishableAccountData + `-${account.openingBalance}`;

            debug('distinguishableAccountData', distinguishableAccountData, account.openingBalance, account.calculatedOpeningBalance);
        }

        if (account.shareholder != null && account.shareholder !== '') {
            distinguishableAccountData = distinguishableAccountData + `-${account.shareholder}`;
        }

        if (account.isCloneAccount) {
            distinguishableAccountData = distinguishableAccountData + account.isCloneAccount;
        }

        return distinguishableAccountData;
    }

    private getDistinguishableAccountDataWithInvertedBalance(account: IAccount, fromPreviousYearAccount: boolean = false): string {
        let distinguishableAccountData: string = `${account.accountNumber}`;


        if (fromPreviousYearAccount) {
            distinguishableAccountData = account.calculatedClosingBalance != null
                ? distinguishableAccountData + `-${NumberUtils.multiplyNumbers([-1, account.calculatedClosingBalance])}`
                :
                distinguishableAccountData + `-${account.closingBalance}`;
            debug('distinguishableAccountData Inverted fromPreviousYearAccount', distinguishableAccountData, account.closingBalance, account.calculatedClosingBalance);

        } else {
            distinguishableAccountData = account.calculatedOpeningBalance != null
                ? distinguishableAccountData + `-${NumberUtils.multiplyNumbers([-1, account.calculatedOpeningBalance])}`
                :
                distinguishableAccountData + `-${account.openingBalance}`;

            debug('distinguishableAccountData Inverted', distinguishableAccountData, account.openingBalance, account.calculatedOpeningBalance);
        }

        if (account.shareholder != null && account.shareholder !== '') {
            distinguishableAccountData = distinguishableAccountData + `-${account.shareholder}`;
        }

        if (account.isCloneAccount) {
            distinguishableAccountData = distinguishableAccountData + account.isCloneAccount;
        }

        return distinguishableAccountData;
    }

    private getDistinguishableAccountDataWithoutBalance(account: IAccount): string {
        let distinguishableAccountData: string = `${account.accountNumber}-${account.name}`;

        if (account.shareholder != null && account.shareholder !== '') {
            distinguishableAccountData = distinguishableAccountData + `-${account.shareholder}`;
        }

        if (account.isCloneAccount) {
            distinguishableAccountData = distinguishableAccountData + account.isCloneAccount;
        }

        return distinguishableAccountData;
    }

    private useSignBasedOnAccountType(currentAccount: IAccount | IManualAddedOrUpdatedAccount, sign: 1 | -1): void {
        if (
            currentAccount.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING &&
            !currentAccount.isBalanceSwitchAccount &&
            !this.balanceSwitchAccounts.find((bsa) => bsa.accountNumber === currentAccount.accountNumber)
        ) {

            if (!('ignoreFliSign' in currentAccount)) {
                currentAccount.calculatedClosingBalance = NumberUtils.multiplyNumbers([
                    currentAccount.closingBalance ?? 0,
                    sign,
                ]);
            } else {
                currentAccount.calculatedClosingBalance = currentAccount.closingBalance;
            }

            // We don't need to make any calculation for summarized accounts if the calculatedClosingBalance is already set
            if (currentAccount.isSummarized && currentAccount.calculatedOpeningBalance != null) {

                if (sign === -1 && currentAccount.calculatedOpeningBalance === currentAccount.openingBalance) {
                    // calculate it again
                    currentAccount.calculatedOpeningBalance = NumberUtils.multiplyNumbers([
                        currentAccount.openingBalance ?? 0,
                        sign,
                    ]);
                }
                if (sign === 1 && currentAccount.calculatedOpeningBalance !== currentAccount.openingBalance) {
                    // calculate it again
                    currentAccount.calculatedOpeningBalance = NumberUtils.multiplyNumbers([
                        currentAccount.openingBalance ?? 0,
                        sign,
                    ]);
                }
                return;
            }

            if (!currentAccount.comingFromPreviousAnnualReport) {
                if (!('ignoreFliSign' in currentAccount)) {
                    currentAccount.calculatedOpeningBalance = NumberUtils.multiplyNumbers([
                        currentAccount.openingBalance ?? 0,
                        sign,
                    ]);
                }
            } else {
                currentAccount.calculatedOpeningBalance = currentAccount.openingBalance;
            }
        }

        // FROM STAGING: Hotfix - Ensure balances are calculated for Balance Switch Accounts
        if (
            currentAccount.isBalanceSwitchAccount &&
            (currentAccount.calculatedClosingBalance == null || currentAccount.calculatedOpeningBalance == null)
        ) {
            // TODO we need to set the calculated balances for balance switch accounts as well
            currentAccount.calculatedClosingBalance = Math.abs(currentAccount.closingBalance);
            currentAccount.calculatedOpeningBalance = Math.abs(currentAccount.openingBalance);
        }
    }

    /**
     * This function will run for autofill when the existing accounts contain values from last year
     */
    private assignAdjustmentsAndReclassesToAccountIfNotPresent(account: IAccount, item: IAccount): void {
        let closingReClass: number | undefined;
        if (account.closingReClass != null && NumberUtils.isFinite(account.closingReClass)) {
            closingReClass = NumberUtils.roundNumber(account.closingReClass);
        } else if (item.closingReClass != null && NumberUtils.isFinite(item.closingReClass)) {
            closingReClass = NumberUtils.roundNumber(item.closingReClass);
        }

        let openingReClass: number | undefined;
        if (account.openingReClass != null && NumberUtils.isFinite(account.openingReClass)) {
            openingReClass = NumberUtils.roundNumber(account.openingReClass);
        } else if (item.openingReClass != null && NumberUtils.isFinite(item.openingReClass)) {
            openingReClass = NumberUtils.roundNumber(item.openingReClass);
        }

        let closingAdjustment: number | undefined;
        if (account.closingAdjustment != null && NumberUtils.isFinite(account.closingAdjustment)) {
            closingAdjustment = NumberUtils.roundNumber(account.closingAdjustment);
        } else if (item.closingAdjustment != null && NumberUtils.isFinite(item.closingAdjustment)) {
            closingAdjustment = NumberUtils.roundNumber(item.closingAdjustment);
        }

        let openingAdjustment: number | undefined;
        if (account.openingAdjustment != null && NumberUtils.isFinite(account.openingAdjustment)) {
            openingAdjustment = NumberUtils.roundNumber(account.openingAdjustment);
        } else if (item.openingAdjustment != null && NumberUtils.isFinite(item.openingAdjustment)) {
            openingAdjustment = NumberUtils.roundNumber(item.openingAdjustment);
        }

        account.closingReClass = closingReClass;
        account.openingReClass = openingReClass;
        account.closingAdjustment = closingAdjustment;
        account.openingAdjustment = openingAdjustment;
    }

    public getNumberFormat(): NumberFormats {
        return this.sheetSystemRules.numberFormat;
    }

    public getDates(): {
        startDate: string;
        endDate: string;
    } {
        return { ...this.dates };
    }

    public getSheetSystemRules(): IFinancialStatementInterpretation {
        return this.sheetSystemRules;
    }

    public updateAccounts(
        accounts: IAccount[],
        engineMode: EngineModes,
        previousDuplicateAccountNumber?: Set<string>,
    ): void {
        this.mode = engineMode;
        const previousAccounts = cloneDeep(this.accounts);
        this.accounts = (accounts || []).filter((account) => account != null);
        this.previousDuplicateAccountNumber = previousDuplicateAccountNumber;

        let switchAccountMappingResults: SwitchAccountMappingResults = undefined;
        // FROM STAGING: Updated condition (Hotfix)
        if (
            this.getTopGroup().name === DefaultGroupsNames.BALANCE_SHEET &&
            this.balanceSwitchAccounts.length !== EMPTY_ARRAY_LENGTH
        ) {
            switchAccountMappingResults = this.recreateSplitAccounts(true);
        }

        // should have the duplicate first then break accounts in previous automatic fill mode
        // prepare duplicate account number set
        this.prepareDuplicateAccountSet();

        //Automap split accounts
        if (switchAccountMappingResults && switchAccountMappingResults.splittedAccounts.length > 0) {
            const validSplittedAccounts = switchAccountMappingResults.splittedAccounts.filter(
                (splittedAccount: SplittedAccount) => splittedAccount?.balanceSwitchAccount?.accountNumber != null,
            );

            //map splitted accounts
            if (this.mode === EngineModes.PREVIOUS_AUTOMATIC_FILL || this.mode === EngineModes.COPY_FROM_OTHER_REPORT) {
                // break account which was duplicate in previous year but not in current year
                this.breakPreviousYearDuplicateCurrentYearSingleAccounts();
            }

            //filter this.accounts to get the splitted accounts
            const splittedAccountsFiltered = this.accounts.filter((account) => {
                if (account?.accountNumber == null) {
                    return false;
                }
                return validSplittedAccounts.some((splittedAccount: SplittedAccount) => {
                    return account.accountNumber === splittedAccount?.balanceSwitchAccount?.accountNumber;
                });
            });

            // Loop through splitted accounts and map them to the correct group
            for (let index = 0; index < splittedAccountsFiltered.length; index++) {
                const account = splittedAccountsFiltered[index];
                if (account?.accountNumber == null) {
                    continue;
                }

                // get balance switch accounts from splitted accounts
                const balanceSwitchAccount: IBalanceSwitchAccount | undefined =
                    validSplittedAccounts.find((splittedAccount: SplittedAccount) => {
                        return account.accountNumber === splittedAccount?.balanceSwitchAccount?.accountNumber;
                    })?.balanceSwitchAccount;

                if (balanceSwitchAccount == null) {
                    continue;
                }
                // determin if this account is Asset or Liabilities account
                let accountOriginFromAssets = (account as unknown as IBalanceSwitchAccount)?.originsFromAsset;
                if (accountOriginFromAssets) {
                    //find and replace here: templateMapping.group.groups[0];
                    this.findAccountToUpdateInGroup(account, DefaultGroupsNames.ASSETS);
                    continue;
                } else {
                    //find and replace here: templateMapping.group.groups[1];
                    this.findAccountToUpdateInGroup(account, DefaultGroupsNames.LIABILITIES);
                    continue;
                }
            }
        }

        this.calculateAll(true);
    }

    // this will update the account in it's place in the accounts array
    public updateAccount(account: IAccount): void {
        this.updateAccountInItsCurrentReference(account);
    }

    private updateAccountInItsCurrentReference(account: IAccount): void {
        // to handle the transferred profit account as it doesn't exist in the accounts dictionary

        if (account.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING) {

            let accountToAssign = this.accountsDictionaryWithAccountNumberAsKey[account.accountNumber];
            Object.assign(
                accountToAssign,
                account,
            );

            const accountIndx = this.accounts.findIndex(
                (excelAccounts) => account.accountNumber === excelAccounts.accountNumber,
            );

            this.accounts[accountIndx] = account;
        }
    }

    public mapIncomeTotalToBalanceProfitFliOrAnnualSurplus(
        templateGroup: IAccountMappingTemplateGroup,
        incomeStatementClosingTotal: number,
        incomeStatementOpeningTotal: number,
        isPreviousDataTakenFromPreviousAnnualReport: boolean,
        mapToAnnualSurplus?: boolean,
    ): void {
        templateGroup.groups.forEach((group: IAccountMappingTemplateGroup) => {
            if (group.items != null) {
                this.setIncomeTotalsInBalanceProfitFliOrAnnualSurplus(
                    group,
                    incomeStatementClosingTotal,
                    incomeStatementOpeningTotal,
                    isPreviousDataTakenFromPreviousAnnualReport,
                    mapToAnnualSurplus,
                );
            }

            // in case groups have groups nested inside them
            if (group.groups != null) {
                this.mapIncomeTotalToBalanceProfitFliOrAnnualSurplus(
                    group,
                    incomeStatementClosingTotal,
                    incomeStatementOpeningTotal,
                    isPreviousDataTakenFromPreviousAnnualReport,
                    mapToAnnualSurplus,
                );
            }
        });
    }

    private setIncomeTotalsInBalanceProfitFliOrAnnualSurplus(
        group: IAccountMappingTemplateGroup,
        incomeStatementClosingTotal: number,
        incomeStatementOpeningTotal: number,
        isPreviousDataTakenFromPreviousAnnualReport: boolean,
        mapToAnnualSurplus?: boolean,
    ): void {
        const fliNameToMapIncome = mapToAnnualSurplus ? this.annualSurplusFliName : this.profitOfTheYearFliName;
        group.items.forEach((fli) => {
            if (fli.name === fliNameToMapIncome) {
                this.transferTotalsIncomeToProfitFli(
                    fli as IAccountMappingTemplateGroupItemFLI,
                    incomeStatementClosingTotal,
                    incomeStatementOpeningTotal,
                    isPreviousDataTakenFromPreviousAnnualReport,
                );
            }
        });
    }

    public setProfitOfTheYearFliName(profitOfTheYear: string) {
        this.profitOfTheYearFliName = profitOfTheYear;
    }

    public setAnnualSurplusFliName(annualSurplus: string) {
        this.annualSurplusFliName = annualSurplus;
    }

    private transferTotalsIncomeToProfitFli(
        fli: IAccountMappingTemplateGroupItemFLI,
        incomeStatementClosingTotal: number,
        incomeStatementOpeningTotal: number,
        isPreviousDataTakenFromPreviousAnnualReport: boolean,
    ): void {
        if (!isPreviousDataTakenFromPreviousAnnualReport) {
            this.removeAccountsAndSubflisFromProfitFli(fli);
            this.createSpecialAccountForIncomeStatementTotalsInBalance(
                fli as IAccountMappingTemplateGroupItemFLI,
                incomeStatementClosingTotal,
                incomeStatementOpeningTotal,
            );
        } else {
            const specialAccount = fli.accounts.find(
                (account) => account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING,
            ) as IAccount;
            if (specialAccount != null) {
                specialAccount.closingBalance = NumberUtils.roundNumber(incomeStatementClosingTotal);

                specialAccount.calculatedClosingBalance = NumberUtils.roundNumber(incomeStatementClosingTotal);
                fli.accounts = [specialAccount];
                this.calculateAll();
            } else {
                this.removeAccountsAndSubflisFromProfitFli(fli);
                this.createSpecialAccountForIncomeStatementTotalsInBalance(
                    fli as IAccountMappingTemplateGroupItemFLI,
                    incomeStatementClosingTotal,
                    incomeStatementOpeningTotal,
                );
            }
        }
    }
    private removeAccountsAndSubflisFromProfitFli(fli: IAccountMappingTemplateGroupItemFLI): void {
        fli.accounts = [];
    }

    private createSpecialAccountForIncomeStatementTotalsInBalance(
        fli: IAccountMappingTemplateGroupItemFLI,
        incomeStatementClosingTotal: number,
        incomeStatementOpeningTotal: number,
    ): void {
        const transferredProfitAccount: IAccount = {
            accountNumber: '',
            name: this.transferredProfitAccountName,
            type: AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING,
            openingBalance: NumberUtils.roundNumber(incomeStatementOpeningTotal),
            closingBalance: NumberUtils.roundNumber(incomeStatementClosingTotal),
            calculatedOpeningBalance: NumberUtils.roundNumber(incomeStatementOpeningTotal),
            calculatedClosingBalance: NumberUtils.roundNumber(incomeStatementClosingTotal),
        };

        fli.accounts.push(transferredProfitAccount);
        this.calculateAll();
    }

    public setSpecialAccountNameOfProfitFli(accountName: string): void {
        this.transferredProfitAccountName = accountName;
    }

    public getAllFlisInMappingTemplate(): IAccountMappingTemplateGroupItemFLI[] {
        const mainGroup = this.getTopGroup();
        return this.getFlisAndSubFlisOfGroups(mainGroup).filter((items) =>
            isFli(items),
        ) as IAccountMappingTemplateGroupItemFLI[];
    }

    public getFlisAndSubFlisForSwitchAccount(
        initialTopGroupName: string,
    ): (IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI)[] {
        let switchAccountGroup: IAccountMappingTemplateGroup | undefined;

        if (initialTopGroupName === DefaultGroupsNames.ASSETS) {
            switchAccountGroup = this.getTopGroups().find((group) => group.name === DefaultGroupsNames.LIABILITIES);
        } else {
            switchAccountGroup = this.getTopGroups().find((group) => group.name === DefaultGroupsNames.ASSETS);
        }

        return this.getFlisAndSubFlisOfGroups(switchAccountGroup);
    }

    public getFlisAndSubFlisOfGroups(
        group: IAccountMappingTemplateGroup,
    ): (IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI)[] {
        const items: (IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI)[] = [];
        if (group.isHeader) return [];
        for (const item of group.sortedItems) {
            if (isFli(item as SortedMappingItem)) {
                items.push(item as IAccountMappingTemplateGroupItemFLI);
                items.push(...this.getSubFlisOfFli(item as IAccountMappingTemplateGroupItemFLI));
            }
            if (isGroup(item as SortedMappingItem)) {
                items.push(...this.getFlisAndSubFlisOfGroups(item as IAccountMappingTemplateGroup));
            }
        }

        return items;
    }

    public removeGroup(group: IAccountMappingTemplateGroup): void {
        const mainGroup = this.getTopGroup();
        const parentGroupAndGroupIndex = this.searchForParentGroupOfGroup(mainGroup, group);

        if (parentGroupAndGroupIndex == undefined) {
            throw new Error('Could not find group in template');
        }
        parentGroupAndGroupIndex.parentGroup.groups.splice(parentGroupAndGroupIndex.indexInGroups, 1);
        parentGroupAndGroupIndex.parentGroup.sortedItems.splice(parentGroupAndGroupIndex.indexInSortedItems, 1);
    }

    public removeItemFromParentGroup(
        item: AccountMappingTemplateGroupItem | IAccountMappingTemplateGroup,
        removeAccounts: boolean,
    ): void {
        if (isHeaderGroup(item) || isEmptyRowGroup(item)) {
            const mainGroup = this.getTopGroup();
            const parentGroupAndGroupIndex = this.searchForParentGroupOfGroup(
                mainGroup,
                item as IAccountMappingTemplateGroup,
            );
            if (parentGroupAndGroupIndex == null) {
                return;
            }
            parentGroupAndGroupIndex.parentGroup.groups.splice(parentGroupAndGroupIndex.indexInGroups, 1);
            parentGroupAndGroupIndex.parentGroup.sortedItems.splice(parentGroupAndGroupIndex.indexInSortedItems, 1);
            return;
        }

        const parentGroupAndFliIndex = this.getParentGroupOfItem(item as AccountMappingTemplateGroupItem);
        if (parentGroupAndFliIndex == null) {
            return;
        }

        if (isFli(item) && removeAccounts) {
            this.removeAllAccountsFromFli(item, parentGroupAndFliIndex.parent.name);
        }
        if (isSubfli(item) && removeAccounts) {
            this.removeAllAccountsFromSubFli(item, parentGroupAndFliIndex.parent.name);
        }
        if (isGroup(parentGroupAndFliIndex.parent)) {
            parentGroupAndFliIndex.parent.items.splice(parentGroupAndFliIndex.indexInItems, 1);
            parentGroupAndFliIndex.parent.sortedItems.splice(parentGroupAndFliIndex.indexInSortedItems, 1);
        } else if (isFli(parentGroupAndFliIndex.parent)) {
            parentGroupAndFliIndex.parent.accounts.splice(parentGroupAndFliIndex.indexInItems, 1);
        }

        return;
    }

    public getParentGroupOfItem(item: AccountMappingTemplateGroupItem): IItemParentGroupAndIndexes | null {
        const mainGroup = this.getTopGroup();
        return this.searchForParentGroupOfItem(mainGroup, item);
    }

    public searchForParentGroupOfItem(
        group: IAccountMappingTemplateGroup,
        item: AccountMappingTemplateGroupItem,
    ): IItemParentGroupAndIndexes | null {
        for (let index = 0; index < group.items.length; index++) {
            const itemInGroup = group.items[index];
            if (itemInGroup.type === item.type && itemInGroup.name === item.name) {
                let itemParentGroupAndIndexes = {} as IItemParentGroupAndIndexes;
                itemParentGroupAndIndexes.parent = group;
                itemParentGroupAndIndexes.indexInItems = index;

                for (let index = 0; index < group.sortedItems.length; index++) {
                    if (
                        (group.sortedItems[index] as AccountMappingTemplateGroupItem).type === item.type &&
                        group.sortedItems[index].name === item.name
                    ) {
                        itemParentGroupAndIndexes.indexInSortedItems = index;
                    }
                }

                return itemParentGroupAndIndexes;
            } else if (isFli(itemInGroup) && (isHeader(item) || isEmpty(item))) {
                for (let index = 0; index < itemInGroup.accounts.length; index++) {
                    const child = itemInGroup.accounts[index];
                    if (child.type === item.type && child.name === item.name) {
                        let itemParentGroupAndIndexes = {
                            parent: itemInGroup,
                            indexInItems: index,
                        } as IItemParentGroupAndIndexes;
                        return itemParentGroupAndIndexes;
                    }
                }
            }
        }

        for (const innerGroup of group.groups) {
            const foundParentGroup = this.searchForParentGroupOfItem(innerGroup, item);
            if (foundParentGroup != null) {
                return foundParentGroup;
            }
        }

        return null;
    }

    public searchForParentGroupOfGroup(
        mainGroup: IAccountMappingTemplateGroup,
        targetGroup: IAccountMappingTemplateGroup,
    ): IGroupParentGroupAndIndexes | undefined {
        // Search through each group in the current group's "groups" array
        for (const nestedGroup of mainGroup.groups) {
            // If the nested group is the target group, return the parent group
            if (nestedGroup.name === targetGroup.name) {
                const indexInGroups = mainGroup.groups.findIndex(
                    (group) => isGroup(group) && group.name === targetGroup.name,
                );
                const indexInSortedItems = mainGroup.sortedItems.findIndex(
                    (sortedItem) => isGroup(sortedItem) && sortedItem.name === targetGroup.name,
                );
                return {
                    parentGroup: mainGroup,
                    indexInGroups,
                    indexInSortedItems,
                };
            }

            // Otherwise, recursively search in the nested group, with the current group as the new parent
            const foundParent = this.searchForParentGroupOfGroup(nestedGroup, targetGroup);
            if (foundParent) {
                return foundParent; // If the target group is found in deeper nested groups, return its parent
            }
        }
        return undefined;
    }

    private getSubFlisOfFli(fli: IAccountMappingTemplateGroupItemFLI): IAccountMappingSubFLI[] {
        const subflis: IAccountMappingSubFLI[] = [];
        for (const item of fli.accounts) {
            if (isSubfli(item)) {
                subflis.push(item);
            }
        }
        return subflis;
    }

    public getOfThatItemSiblings(ofThatItem: IAccountMappingSubFLI): IAccountMappingSubFLI[] {
        const allFlis = this.getAllFlisInMappingTemplate();
        let parentFli: IAccountMappingTemplateGroupItemFLI | undefined;
        for (const fli of allFlis) {
            if (fli.accounts.includes(ofThatItem)) {
                parentFli = fli;
                break;
            }
        }
        if (parentFli == null) {
            throw new Error('could not find a parent fli for the ofThatItem');
        }
        return this.getSubFlisOfFli(parentFli).filter((item) => item.name !== ofThatItem.name && isOfThatItem(item));
    }

    public getSplitAccountValues(
        originalClosingValue: number,
        originalOpeningValue: number,
        accountTopParentGroupName: string,
    ): ISplitAccountValues {
        const isAssetAccount = accountTopParentGroupName === DefaultGroupsNames.ASSETS;

        const assignValue = (value: number, toAssets: boolean) => ({
            assetValue: toAssets ? value : 0,
            liabilityValue: toAssets ? 0 : value,
        });

        const closingAssignment = isAssetAccount
            ? assignValue(originalClosingValue, originalClosingValue > 0)
            : assignValue(originalClosingValue, originalClosingValue <= 0);

        const openingAssignment = isAssetAccount
            ? assignValue(originalOpeningValue, originalOpeningValue > 0)
            : assignValue(originalOpeningValue, originalOpeningValue <= 0);

        return {
            assetsClosingValue: Math.abs(closingAssignment.assetValue),
            assetsOpeningValue: Math.abs(openingAssignment.assetValue),
            liabilitiesClosingValue: Math.abs(closingAssignment.liabilityValue),
            liabilitiesOpeningValue: Math.abs(openingAssignment.liabilityValue),
        };
    }

    public splitAccount(
        account: IAccount,
        accountParentItem: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
        accountIndex: number,
        accountTopParentGroupName: string,
        splitParentItem: IAccountMappingSubFLI | IAccountMappingTemplateGroupItemFLI,
    ): void {
        const origin = this.balanceSwitchAccountService.determineAccountOrigin(
            accountParentItem,
            accountTopParentGroupName,
        );
        const splittedAccounts: SplittedAccount = this.balanceSwitchAccountService.splitAccount(
            this.sheetSystemRules,
            account,
            origin,
        );

        if (splittedAccounts?.balanceSwitchAccount?.accountNumber == null) {
            console.warn('[AccountMappingEngine] Split account result is missing balanceSwitchAccount.accountNumber, split skipped.');
            return;
        }

        this.addOrUpdateAccount(account, splittedAccounts.originalAccount, accountParentItem, accountIndex);
        // add account (couterpart)
        const newIndex = splitParentItem.accounts.length;
        this.addOrUpdateAccount(null, splittedAccounts.counterpartAccount, splitParentItem, newIndex);

        this.duplicateAccountNumberSet.add(splittedAccounts.balanceSwitchAccount.accountNumber);
        this.balanceSwitchAccounts.push(splittedAccounts.balanceSwitchAccount);

        if (isFli(splitParentItem) || isSubfli(splitParentItem)) {
            splitParentItem.accounts = this.sortAccounts(splitParentItem.accounts);
        }
    }

    private removeBalanceSwitchAccountFromFLIOrSubFLI(
        item: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        index: number,
        accountTopGroupName: string,
    ): void {
        // balance switch account is a duplicate account
        // we need to unmap the 2 duplicate accounts
        const accountNumber = (item.accounts[index] as IAccount).accountNumber;
        // remove first duplicate account
        item.accounts = item.accounts.filter((_, indexOfItem) => indexOfItem !== index);
        // find other duplicate account in other group
        let otherGroupName = '';
        if (accountTopGroupName === DefaultGroupsNames.ASSETS) {
            otherGroupName = DefaultGroupsNames.LIABILITIES;
        } else {
            otherGroupName = DefaultGroupsNames.ASSETS;
        }

        // remove other duplicate account in other group
        this.removeAccountByGroupName(otherGroupName, accountNumber);

        this.convertBalanceSwitchAccountToNormalAccount(accountNumber);
    }

    public removeAccountByGroupName(groupName: string, accountNumber: string) {
        const group = this.getTopGroups().find((group) => group.name === groupName);

        const groupItems = this.getFlisAndSubFlisOfGroups(group);
        for (const item of groupItems) {
            if (isFli(item)) {
                const accountIndex = item.accounts.findIndex(
                    (account) =>
                        account.type !== ItemType.SUB_FLI && (account as IAccount).accountNumber === accountNumber,
                );
                if (accountIndex > -1) {
                    // remove account
                    item.accounts.splice(accountIndex, 1);

                    break;
                }
                continue;
            }

            if (isSubfli(item)) {
                const accountIndex = item.accounts.findIndex((account) => account.accountNumber === accountNumber);
                if (accountIndex > -1) {
                    // remove account
                    item.accounts.splice(accountIndex, 1);

                    break;
                }
            }
        }
    }

    /**
     * REV-21304: rebuilds the merged (un-split) raw opening/closing of a balance switch account from
     * its two CURRENT legs in `this.accounts`. Each leg stores a positive magnitude on its own side, so
     * the true signed value is (asset-side leg) − (liability-side leg) for each period. This reflects the
     * current year and any in-place edits. Falls back to the summary's own balances if a leg is missing.
     */
    private reconstructMergedBalancesFromLegs(
        accountNumber: string,
        summary: IBalanceSwitchAccount,
    ): { openingBalance: number; closingBalance: number } {
        const legs = this.accounts.filter(
            (acc) => acc != null && acc.accountNumber === accountNumber && acc.isBalanceSwitchAccount === true,
        ) as IBalanceSwitchAccount[];
        const assetLeg = legs.find((l) => l.originsFromAsset === true);
        const liabilityLeg = legs.find((l) => l.originsFromAsset === false);

        if (assetLeg == null && liabilityLeg == null) {
            // No legs to reconstruct from — keep the summary's current (healed) balances.
            return { openingBalance: summary.openingBalance, closingBalance: summary.closingBalance };
        }

        const magnitude = (leg: IBalanceSwitchAccount | undefined, period: 'opening' | 'closing'): number => {
            if (leg == null) {
                return 0;
            }
            const calculated = period === 'opening' ? leg.calculatedOpeningBalance : leg.calculatedClosingBalance;
            if (calculated != null && NumberUtils.isFinite(calculated)) {
                return Math.abs(calculated);
            }
            const raw = period === 'opening' ? leg.openingBalance : leg.closingBalance;
            return Math.abs(raw ?? 0);
        };

        // BUGFIX (REV-21304, Bug A): orient the merge by the account's ORIGIN. Each leg stores a positive
        // magnitude, so the raw signed value must be rebuilt from which side is the account's "home":
        //   - asset-origin  (a bank): the natural positive sits on the ASSET leg → merged = asset − liability.
        //   - liability-origin (a credit card): the legs are MIRRORED (its natural positive sits on the
        //     LIABILITY leg) → merged = liability − asset.
        // The previous code used (asset − liability) unconditionally, which flipped the signs when
        // un-mapping a liability-origin switch account (e.g. +1'500 / -1'000 came back as -1'500 / +1'000).
        // Default to asset-origin when the summary's flag is absent (preserves the prior behaviour).
        const originIsAsset = summary?.originsFromAsset !== false;
        const assetOpening = magnitude(assetLeg, 'opening');
        const assetClosing = magnitude(assetLeg, 'closing');
        const liabilityOpening = magnitude(liabilityLeg, 'opening');
        const liabilityClosing = magnitude(liabilityLeg, 'closing');

        return originIsAsset
            ? {
                  openingBalance: assetOpening - liabilityOpening,
                  closingBalance: assetClosing - liabilityClosing,
              }
            : {
                  openingBalance: liabilityOpening - assetOpening,
                  closingBalance: liabilityClosing - assetClosing,
              };
    }

    private convertBalanceSwitchAccountToNormalAccount(accountNumber: string): void {
        const originalAccountIndex = this.balanceSwitchAccounts.findIndex((acc) => acc.accountNumber === accountNumber);

        if (originalAccountIndex < 0) {
            console.warn(
                `[AccountMappingEngine] tried to convert switch‐account ${accountNumber},` +
                ` but no such balanceSwitchAccount exists.`,
            );
            return; // or `throw new Error(...)` if you’d rather fail hard
        }

        // pull it out once so we know it's defined
        const originalAccount = this.balanceSwitchAccounts[originalAccountIndex]!;

        // remove it from the switch array
        this.balanceSwitchAccounts.splice(originalAccountIndex, 1);

        // BUGFIX (REV-21304, round 2): reconstruct the merged raw balances from the account's CURRENT
        // two split legs — the asset-side leg minus the liability-side leg — NOT from the summary's
        // `original{Opening,Closing}Balance`, which is a FIRST-YEAR snapshot. Using original* returned
        // the year-1 values when un-mapping a later year (issue 1) and the pre-edit values when
        // un-mapping after editing a leg (issue 2). The legs always hold the current, edited, correctly
        // -signed magnitudes, so (asset − liability) is the true merged value in every case.
        const merged = this.reconstructMergedBalancesFromLegs(accountNumber, originalAccount as IBalanceSwitchAccount);

        // find where it used to sit in the "normal" list
        const accountIndex = this.accounts.findIndex((acc) => acc.accountNumber === accountNumber);

        // remove any stale duplicates (both split legs)
        this.accounts = this.accounts.filter((acc) => acc.accountNumber !== accountNumber);

        originalAccount.openingBalance = merged.openingBalance;
        originalAccount.closingBalance = merged.closingBalance;
        originalAccount.calculatedOpeningBalance = merged.openingBalance;
        originalAccount.calculatedClosingBalance = merged.closingBalance;

        // strip out the switch-only fields
        delete originalAccount.balanceSwitchAccountId;
        delete originalAccount.originsFromAsset;
        delete originalAccount.originalClosingBalance;
        delete originalAccount.originalOpeningBalance;
        originalAccount.isBalanceSwitchAccount = false;

        // if the original spot exists, put it back there; otherwise push to the end
        const insertionIndex = accountIndex >= 0 ? accountIndex : this.accounts.length;

        this.accounts.splice(insertionIndex, 0, originalAccount);
    }

    /**
     * REV-21304: repairs a balance switch account whose `closingBalance`/`openingBalance` sign was
     * corrupted by the double-save bug, mutating it in place so every downstream consumer (candidate
     * detection, splitting, previous-year carry) sees the correct signs. The raw values are recovered
     * from `originalClosingBalance`/`originalOpeningBalance` first, then a sign-reversed calculated
     * balance, then the (possibly corrupted) raw value. Calculated balances are kept consistent with
     * the account's interpretation. Only balance switch accounts are touched.
     */
    private healCorruptedSwitchAccountSigns(account: IBalanceSwitchAccount): void {
        if (account == null || !account.isBalanceSwitchAccount) {
            return;
        }
        const reliable = BalanceSwitchAccountService.getReliableRawBalances(account);
        account.openingBalance = reliable.openingBalance;
        account.closingBalance = reliable.closingBalance;
        if (account.interpretation === Signs.NEGATIVE) {
            account.calculatedOpeningBalance = -1 * reliable.openingBalance + 0;
            account.calculatedClosingBalance = -1 * reliable.closingBalance + 0;
        } else {
            account.calculatedOpeningBalance = reliable.openingBalance;
            account.calculatedClosingBalance = reliable.closingBalance;
        }
    }

    private recreateSplitAccounts(enforce: boolean): SwitchAccountMappingResults {
        //Guard to only create the split accounts once
        if (this.createSplitAccountsAutomaticallyDone == false || enforce) {
            this.previousSwitchAccounts = cloneDeep(this.balanceSwitchAccounts);
            this.createSplitAccountsAutomaticallyDone = true;
        } else {
            return {
                splittedAccounts: [],
                mergedAccounts: [],
            };
        }
        const balanceSwitchCandidates: Array<IAccount | IBalanceSwitchAccount> = [];
        const temporaryAccountsCopy = cloneDeep<IAccount[]>(this.accounts);
        const newBalanceSwitchAccounts: IBalanceSwitchAccount[] = [];
        const splittedAccounts: SplittedAccount[] = [];
        const mergedAccounts: MergedAccount[] = [];

        // We don't proceed if there are no accounts
        if (temporaryAccountsCopy == null || temporaryAccountsCopy.length === 0) {
            return {
                splittedAccounts: [],
                mergedAccounts: [],
            };
        }

        for (const account of temporaryAccountsCopy) {
            if (account == null) {
                continue;
            }

            // BUGFIX (REV-21304, issue 3): never auto-re-split an account the user has EXPLICITLY
            // un-mapped (isBalanceSwitchAccount === false). Such a demoted account can still carry a
            // leftover `originsFromAsset` from when it was a switch; without this guard the raw
            // opposite-sign scan re-splits it into two positive legs on every reload — flipping a
            // "-1'000 / 1'500" account into "both positive". A genuinely new switch account has
            // isBalanceSwitchAccount `undefined` (not `false`), so it is unaffected.
            if (account.isBalanceSwitchAccount === false) {
                continue;
            }

            if (this.balanceSwitchAccountService.isBalanceSwitchCandidate(account)) {
                balanceSwitchCandidates.push(account);
            }
        }

        // 2. Process existing BSA array (Handle Upgrades)
        for (const balanceSwitchAccount of this.balanceSwitchAccounts) {

            // CASE A: New Implementation -> Push as is
            const accountToCheck = balanceSwitchAccount as IBalanceSwitchAccount;
            if ('balanceSwitchAccountId' in balanceSwitchAccount) {
                // BUGFIX (REV-21304): when carrying a PREVIOUS year's switch account into an autofill /
                // copy run, its saved `closingBalance` may have been sign-corrupted by the double-save
                // bug (+1'000 instead of -1'000). A wrong sign makes the carried previous-year value
                // land on the wrong leg (asset instead of liability). Heal the candidate from the
                // trustworthy raw balances (originalClosingBalance → sign-reversed calculated → raw)
                // before it drives the split. MANUAL mode is left untouched: it re-derives the values
                // sign-correctly from the two split legs in BalanceSwitchAccountService.splitAccount.
                if (this.mode !== EngineModes.MANUAL) {
                    this.healCorruptedSwitchAccountSigns(accountToCheck);
                }
                balanceSwitchCandidates.push(balanceSwitchAccount);
            }

            // CASE B: Old Implementation -> REPAIR & PUSH
            else if (accountToCheck.isBalanceSwitchAccount && this.mode !== EngineModes.MANUAL) {

                const freshData = temporaryAccountsCopy.find(
                    acc => acc.accountNumber === accountToCheck.accountNumber
                );

                // Find old mapped objects to determine origin
                const assetItems = this.getFlisAndSubFlisForSwitchAccount(DefaultGroupsNames.ASSETS);
                const liabilityItems = this.getFlisAndSubFlisForSwitchAccount(DefaultGroupsNames.LIABILITIES);

                const findAcc = (items: any[], id: string) =>
                    items.flatMap(i => i.accounts).find((a: IAccount) => a.accountNumber === id);

                const oldAsset = findAcc(assetItems, accountToCheck.accountNumber);
                const oldLiability = findAcc(liabilityItems, accountToCheck.accountNumber);

                if (freshData) {
                    // --- CALL THE SIMPLIFIED REPAIR METHOD ---
                    const repairedCandidate = BalanceSwitchAccountService.repairOldSwitchAccount(
                        freshData,
                        oldAsset,
                        oldLiability,
                        this.sheetSystemRules.assets,
                        this.sheetSystemRules.liabilities,
                    );

                    // --- PUSH TO CANDIDATES ---
                    // This ensures it gets processed by the standard logic loop below
                    balanceSwitchCandidates.push(repairedCandidate);
                }
            }
        }

        //filter out duplicates from balanceSwitchCandidates
        const uniqueBalanceSwitchCandidates = balanceSwitchCandidates.filter(
            (account, index, self) => index === self.findIndex((a) => a.accountNumber === account.accountNumber),
        );

        // Check all balance swtich candidates
        for (const account of uniqueBalanceSwitchCandidates) {
            let origin = undefined;
            if ('originsFromAsset' in account) {
                if (account.originsFromAsset) {
                    origin = AccountOrigin.ASSETS;
                } else {
                    origin = AccountOrigin.LIABILITIES;
                }
            }
            try {
                const { originalAccount, counterpartAccount, balanceSwitchAccount } =
                    this.balanceSwitchAccountService.splitAccount(
                        this.sheetSystemRules,
                        account,
                        origin,
                        temporaryAccountsCopy,
                    );

                if (balanceSwitchAccount?.accountNumber == null) {
                    console.warn(
                        `[AccountMappingEngine] Failed to split account ${account.accountNumber}: missing balanceSwitchAccount.accountNumber`,
                    );
                    continue;
                }

                // Remove all instances of balanceSwitchAccount from this.accounts
                this.accounts = this.accounts.filter(
                    (acc) => !(acc.accountNumber === balanceSwitchAccount.accountNumber),
                );

                this.accounts.push(originalAccount);
                this.accounts.push(counterpartAccount);
                newBalanceSwitchAccounts.push(balanceSwitchAccount);

                splittedAccounts.push({
                    originalAccount,
                    counterpartAccount,
                    balanceSwitchAccount,
                });
            } catch (error) {
                console.warn(
                    `[AccountMappingEngine] Failed to split account ${account.accountNumber}: ${error.message}`,
                );
            }
        }

        this.balanceSwitchAccounts = newBalanceSwitchAccounts;
        return { splittedAccounts, mergedAccounts };
    }

    public applyUncoveredShortfall(): void {
        if (this.templateMapping.equityGroupName == null) {
            return;
        }

        if (
            this.uncoveredShortfallSpecifications == null ||
            !this.uncoveredShortfallSpecifications.applyUncoveredShortfall
        ) {
            return;
        }

        const equityGroup = AccountMappingEngineHelper.getNestedGroupByName(
            this.templateMapping.group,
            this.templateMapping.equityGroupName,
        );

        if (equityGroup == null) {
            throw new Error('equity group name does not exist in mapping template');
        }

        if (equityGroup.calculatedClosingBalance >= 0 && equityGroup.calculatedOpeningBalance >= 0) {
            // if uncovered shortfall accounts already existed and mapping was updated that now both values are positive, we remove the uncovered shortfall accounts
            this.removeUncoveredShortfallAccounts();

            return;
        }

        if (this.uncoveredShortfallSpecifications.uncoveredShortfallAssetsFli == null) {
            // create internal fli manually to hold the uncovered shortfall account
            const uncoveredShortfallAssetsFli = AccountMappingEngineHelper.createUncoveredShortfallAssetsFli(
                this.uncoveredShortfallSpecifications.uncoveredShortfallAssetsFliCaption,
            );
            this.addFli(this.getTopGroup().groups[FIRST_ELEMENT_INDEX], uncoveredShortfallAssetsFli);
            this.uncoveredShortfallSpecifications.uncoveredShortfallAssetsFli = uncoveredShortfallAssetsFli.name;
            this.templateMapping.uncoveredShortfallAssetsFli = uncoveredShortfallAssetsFli.name;
        }

        if (this.uncoveredShortfallSpecifications.uncoveredShortfallLiabilitiesFli == null) {
            // create internal fli manually to hold the uncovered shortfall account
            const uncoveredShortfallEquityFli = AccountMappingEngineHelper.createUncoveredShortfallEquityFli(
                this.uncoveredShortfallSpecifications.uncoveredShortfallEquityFliCaption,
            );
            this.addFli(equityGroup, uncoveredShortfallEquityFli);
            this.uncoveredShortfallSpecifications.uncoveredShortfallLiabilitiesFli = uncoveredShortfallEquityFli.name;
            this.templateMapping.uncoveredShortfallLiabilitiesFli = uncoveredShortfallEquityFli.name;
        }

        this.addEquityShortfallAccounts(equityGroup.calculatedClosingBalance, equityGroup.calculatedOpeningBalance);
    }

    private addEquityShortfallAccounts(
        equityGroupCalculatedClosingBalance: number,
        equityGroupCalculatedOpeningBalance: number,
    ): void {
        const assetsFli = AccountMappingEngineHelper.getFliFromNestedGroupsByName(
            this.getTopGroup(),
            this.uncoveredShortfallSpecifications.uncoveredShortfallAssetsFli,
        );

        if (assetsFli == null) {
            return;
        }

        let accountIndexInAssetsFli = assetsFli.accounts.findIndex(
            (account) => account.type === AccountType.UNCOVERED_SHORTFALL,
        );

        if (accountIndexInAssetsFli > -1) {
            // if uncovered shortfall account was created before, then subtract its value from the equity total and remove it to be replaced with the recalculated accounts

            this.removeUncoveredShortfallAccounts();
        }

        const uncoveredShortfallAssetsAccount = AccountMappingEngineHelper.createUncoveredShortfallAccount(
            equityGroupCalculatedClosingBalance,
            equityGroupCalculatedOpeningBalance,
            this.uncoveredShortfallSpecifications.uncoveredShortfallAssetsAccountName,
            'uncoveredShortfall-assets',
        );

        this.addOrUpdateAccount(
            uncoveredShortfallAssetsAccount,
            uncoveredShortfallAssetsAccount,
            assetsFli,
            assetsFli.accounts.length,
        );

        const equityFli = AccountMappingEngineHelper.getFliFromNestedGroupsByName(
            this.getTopGroup(),
            this.uncoveredShortfallSpecifications.uncoveredShortfallLiabilitiesFli,
        );

        if (equityFli == null) {
            return;
        }

        const uncoveredShortfallLiabilitiesAccount = AccountMappingEngineHelper.createUncoveredShortfallAccount(
            equityGroupCalculatedClosingBalance,
            equityGroupCalculatedOpeningBalance,
            this.uncoveredShortfallSpecifications.uncoveredShortfallEquityAccountName,
            'uncoveredShortfall-liabilities',
        );

        this.addOrUpdateAccount(
            uncoveredShortfallLiabilitiesAccount,
            uncoveredShortfallLiabilitiesAccount,
            equityFli,
            equityFli.accounts.length,
        );
    }

    public recalculateUncoveredShortfall(): void {
        if (
            this.uncoveredShortfallSpecifications == null ||
            !this.uncoveredShortfallSpecifications.applyUncoveredShortfall ||
            this.uncoveredShortfallSpecifications.uncoveredShortfallAssetsFli == null ||
            this.uncoveredShortfallSpecifications.uncoveredShortfallLiabilitiesFli == null
        ) {
            return;
        }

        const equityFli = AccountMappingEngineHelper.getFliFromNestedGroupsByName(
            this.templateMapping.group,
            this.uncoveredShortfallSpecifications.uncoveredShortfallLiabilitiesFli,
        );

        const isUncoveredShortfallFliExist = equityFli?.accounts.find(
            (acc) => acc.type === AccountType.UNCOVERED_SHORTFALL,
        );

        if (isUncoveredShortfallFliExist == null) {
            return;
        }

        // if uncoveredShortfall exists in mapping, reapply it to update its values
        this.applyUncoveredShortfall();
        this.calculateAll();
    }

    private removeUncoveredShortfallAccounts(): void {
        if (
            this.uncoveredShortfallSpecifications.uncoveredShortfallAssetsFli == null ||
            this.uncoveredShortfallSpecifications.uncoveredShortfallLiabilitiesFli == null
        ) {
            throw new Error('uncoveredShortfallAssetsFli or uncoveredShortfallLiabilitiesFli is undefined');
        }

        const assetsFli = AccountMappingEngineHelper.getFliFromNestedGroupsByName(
            this.templateMapping.group,
            this.uncoveredShortfallSpecifications.uncoveredShortfallAssetsFli,
        );

        if (assetsFli != null) {
            assetsFli.accounts = assetsFli.accounts.filter((acc) => acc.type !== AccountType.UNCOVERED_SHORTFALL);
        }

        const equityFLi = AccountMappingEngineHelper.getFliFromNestedGroupsByName(
            this.templateMapping.group,
            this.uncoveredShortfallSpecifications.uncoveredShortfallLiabilitiesFli,
        );

        if (equityFLi != null) {
            equityFLi.accounts = equityFLi.accounts.filter((acc) => acc.type !== AccountType.UNCOVERED_SHORTFALL);
        }

        this.accounts = this.accounts.filter((acc) => acc.type !== AccountType.UNCOVERED_SHORTFALL);
    }

    public changeUncoveredShortfallFlis(newAssetsFliName: string | null, newLiabilitiesFliName: string | null): void {
        if (this.uncoveredShortfallSpecifications == null) {
            return;
        }

        const prevAssets = this.uncoveredShortfallSpecifications.uncoveredShortfallAssetsFli;
        const prevLiab = this.uncoveredShortfallSpecifications.uncoveredShortfallLiabilitiesFli;

        if (prevAssets != null && prevLiab != null) {
            this.removeUncoveredShortfallAccounts();
        }

        if (prevAssets === 'uncovered-shortfall-assets-fli' && newAssetsFliName !== 'uncovered-shortfall-assets-fli') {
            this.removeFliFromGroup(this.templateMapping.group, 'uncovered-shortfall-assets-fli');
        }
        if (prevLiab === 'uncovered-shortfall-equity-fli' && newLiabilitiesFliName !== 'uncovered-shortfall-equity-fli') {
            this.removeFliFromGroup(this.templateMapping.group, 'uncovered-shortfall-equity-fli');
        }

        this.uncoveredShortfallSpecifications.uncoveredShortfallAssetsFli = newAssetsFliName ?? undefined;
        this.uncoveredShortfallSpecifications.uncoveredShortfallLiabilitiesFli = newLiabilitiesFliName ?? undefined;
        this.templateMapping.uncoveredShortfallAssetsFli = newAssetsFliName ?? undefined;
        this.templateMapping.uncoveredShortfallLiabilitiesFli = newLiabilitiesFliName ?? undefined;

        this.calculateAll();
        if (newAssetsFliName != null && newLiabilitiesFliName != null) {
            this.applyUncoveredShortfall();
            this.calculateAll();
        }
    }

    private removeFliFromGroup(group: IAccountMappingTemplateGroup, fliName: string): boolean {
        const hasItem = group.items.some((item) => item.type === ItemType.FLI && item.name === fliName);
        if (hasItem) {
            group.items = group.items.filter((item) => !(item.type === ItemType.FLI && item.name === fliName));
            group.sortedItems = (group.sortedItems as SortedMappingItem[]).filter((item) => item.name !== fliName);
            return true;
        }
        for (const subgroup of group.groups) {
            if (this.removeFliFromGroup(subgroup, fliName)) {
                return true;
            }
        }
        return false;
    }

    public getItemIndexInsideGroup(
        item: AccountMappingTemplateGroupItem,
        parent: IAccountMappingTemplateGroup,
    ): number {
        return parent.items.findIndex((group) => group.name === item.name);
    }

    public getSubGroupIndexInsideGroup(
        item: IAccountMappingTemplateGroup,
        parent: IAccountMappingTemplateGroup,
    ): number {
        return parent.groups.findIndex((group) => group.name === item.name);
    }

    public getItemIndexInsideSortedItems(
        item: IAccountMappingTemplateGroup | AccountMappingTemplateGroupItem,
        parent: IAccountMappingTemplateGroup,
    ): number {
        if (item.hasOwnProperty('groups')) {
            return parent.sortedItems.findIndex(
                (sortedItem) => sortedItem.name === item.name && sortedItem.hasOwnProperty('groups'),
            );
        }

        return parent.sortedItems.findIndex(
            (sortedItem) =>
                sortedItem.name === item.name && sortedItem.type === (item as AccountMappingTemplateGroupItem).type,
        );
    }

    public getSubfliOrOfThatItemIndexInsideFli(
        item: AccountMappingAccountItem,
        parent: IAccountMappingTemplateGroupItemFLI,
    ): number {
        return parent.accounts.findIndex((child) => child.name === item.name);
    }

    public getAccountIndexInsideFli(account: IAccount, parent: IAccountMappingTemplateGroupItemFLI): number {
        return parent.accounts.findIndex((child: IAccount) => child.accountNumber === account.accountNumber);
    }

    public insertItemInItemsOfGroup(
        newItem: AccountMappingTemplateGroupItem,
        siblingItemIndex: number,
        parent: IAccountMappingTemplateGroup,
    ): void {
        parent.items.splice(siblingItemIndex, 0, newItem);
    }

    public insertItemInFli(
        newItem: AccountMappingAccountItem,
        siblingItemIndex: number,
        parent: IAccountMappingTemplateGroupItemFLI,
    ): void {
        parent.accounts.splice(siblingItemIndex, 0, newItem);
    }

    public insertItemInGroupsOfGroup(
        newItem: IAccountMappingTemplateGroup,
        siblingItemIndex: number,
        parent: IAccountMappingTemplateGroup,
    ): void {
        parent.groups.splice(siblingItemIndex, 0, newItem);
    }

    public insertItemInSortedItems(
        newItem: IAccountMappingTemplateGroup | AccountMappingTemplateGroupItem,
        siblingItemIndex: number,
        parent: IAccountMappingTemplateGroup,
    ): void {
        parent.sortedItems.splice(siblingItemIndex, 0, newItem);
    }

    public getSavedHeadersNames(group: IAccountMappingTemplateGroup): Set<string> {
        const savedHeadersNames = new Set<string>([]);

        group.items.forEach((item) => {
            if (isHeader(item)) {
                savedHeadersNames.add(item.name);
            } else if (isFli(item)) {
                item.accounts.forEach((account) => {
                    if (isHeader(account)) {
                        savedHeadersNames.add(account.name);
                    }
                });
            }
        });

        return savedHeadersNames;
    }

    public getAllGroupsNamesToGroupsMap(): Map<string, IAccountMappingTemplateGroup> {
        const allGroupsNamesToGroupsMap: Map<string, IAccountMappingTemplateGroup> = new Map<
            string,
            IAccountMappingTemplateGroup
        >();

        return this.setGroupsNamesToGroupsMap(this.templateMapping.group, allGroupsNamesToGroupsMap);
    }

    private setGroupsNamesToGroupsMap(
        group: IAccountMappingTemplateGroup,
        groupsNamesToGroupsMap: Map<string, IAccountMappingTemplateGroup>,
    ): Map<string, IAccountMappingTemplateGroup> {
        let newGroupsNamesToGroupsMap: Map<string, IAccountMappingTemplateGroup> = new Map<
            string,
            IAccountMappingTemplateGroup
        >([...groupsNamesToGroupsMap]);

        newGroupsNamesToGroupsMap.set(group.name, group);

        group.groups.forEach((group) => {
            newGroupsNamesToGroupsMap.set(group.name, group);
            newGroupsNamesToGroupsMap = this.setGroupsNamesToGroupsMap(group, newGroupsNamesToGroupsMap);
        });

        return newGroupsNamesToGroupsMap;
    }

    public getAccountTopGroupName(targetAccount: IAccount): string | undefined {
        for (const group of this.getTopGroups()) {
            const groupItems = this.getFlisAndSubFlisOfGroups(group);
            for (const item of groupItems) {
                if (isFli(item)) {
                    const accountIndex = item.accounts.findIndex(
                        (account) =>
                            account.type !== ItemType.SUB_FLI &&
                            (account as IAccount).accountNumber === targetAccount.accountNumber,
                    );
                    if (accountIndex > -1) {
                        return group.name;
                    }
                }

                if (isSubfli(item)) {
                    const accountIndex = item.accounts.findIndex(
                        (account) => account.accountNumber === targetAccount.accountNumber,
                    );
                    if (accountIndex > -1) {
                        return group.name;
                    }
                }
            }
        }
        return undefined;
    }

    public cloneAccount(
        masterAccount: IAccount,
        masterParent: IAccountMappingSubFLI,
        targetOfThatItems: IAccountMappingSubFLI[],
        index: number,
    ): void {
        // Add clone flag to master account
        this.addOrUpdateAccount(
            masterAccount,
            { ...masterAccount, isMasterCloneAccount: true } as IAccount,
            masterParent,
            index,
        );

        let numberOfCloneAccounts = 0;

        // add clone accounts

        for (const ofThatItem of targetOfThatItems) {
            const clonedAccount: IAccount = cloneDeep<IAccount>(masterAccount);
            clonedAccount.isCloneAccount = true;
            clonedAccount.rowNo = this.getAddedAccountRowNumber();

            const newIndex = ofThatItem.accounts.length;

            this.addOrUpdateAccount(undefined, clonedAccount, ofThatItem, newIndex);
            numberOfCloneAccounts = numberOfCloneAccounts + 1;
        }

        // update masterCloneAccountsToNumberOfCloneAccountsMap

        if (this.masterCloneAccountsToNumberOfCloneAccountsMap.has(masterParent.accounts[index])) {
            const exitingNumberOfCloneAccounts = this.masterCloneAccountsToNumberOfCloneAccountsMap.get(
                masterParent.accounts[index],
            );
            this.masterCloneAccountsToNumberOfCloneAccountsMap.set(
                masterParent.accounts[index],
                exitingNumberOfCloneAccounts + numberOfCloneAccounts,
            );
        } else {
            this.masterCloneAccountsToNumberOfCloneAccountsMap.set(masterParent.accounts[index], numberOfCloneAccounts);
        }
    }

    private removeCloneAccountsOfMasterAccount(
        masterAccount: IAccount,
        masterAccountParent: IAccountMappingSubFLI,
    ): void {
        const ofThatItemSiblings = this.getOfThatItemSiblings(masterAccountParent);

        for (const ofThatItem of ofThatItemSiblings) {
            ofThatItem.accounts.forEach((account: IAccount, index: number) => {
                // if condition to make sure that account is a clone of the master account
                if (
                    account.isCloneAccount &&
                    masterAccount.accountNumber === account.accountNumber &&
                    masterAccount.name === account.name &&
                    masterAccount.calculatedClosingBalance === account.calculatedClosingBalance &&
                    masterAccount.calculatedOpeningBalance === account.calculatedOpeningBalance
                ) {
                    this.removeAccountFromSubFLI(ofThatItem, index);

                    // Remove from accounts array
                    const accountIndexInAccountsArray = this.accounts.findIndex(
                        (acc) => acc.accountNumber === masterAccount.accountNumber && acc.isCloneAccount,
                    );
                    this.accounts.splice(accountIndexInAccountsArray, 1);
                }
            });
        }
    }

    private updateCloneAccountsOfMasterAccount(
        masterAccount: IAccount,
        masterAccountParent: IAccountMappingSubFLI,
    ): void {
        const ofThatItemSiblings = this.getOfThatItemSiblings(masterAccountParent);

        for (const ofThatItem of ofThatItemSiblings) {
            ofThatItem.accounts.forEach((account: IAccount, index: number) => {
                // if condition to make sure that account is a clone of the master account
                if (account.isCloneAccount && masterAccount.accountNumber === account.accountNumber) {
                    const cloneAccount: IAccount = cloneDeep<IAccount>(masterAccount);
                    delete cloneAccount.isMasterCloneAccount;
                    cloneAccount.isCloneAccount = true;
                    this.addOrUpdateAccount(account, cloneAccount, ofThatItem, index);
                }
            });
        }
    }

    private sortAccounts(
        accounts: AccountMappingAccountItem[],
    ): AccountMappingAccountItem[] {
        const accountItems = accounts.filter((i) => isAccount(i)) as IAccount[];
        const otherItems = accounts.filter((i) => !isAccount(i));

        accountItems.sort((a, b) => {
            const numA = parseFloat(a.accountNumber);
            const numB = parseFloat(b.accountNumber);

            if (!isNaN(numA) && !isNaN(numB)) {
                if (numA !== numB) {
                    return numA - numB;
                }
            } else {
                const comparison = a.accountNumber.localeCompare(b.accountNumber);
                if (comparison !== 0) {
                    return comparison;
                }
            }

            return a.name.localeCompare(b.name);
        });

        return [...otherItems, ...accountItems];
    }

    // FROM STAGING: Hotfix Helpers for Copy From Other Report
    private initEngineForCopyFromOtherReport(): void {
        this.balanceSwitchAccountService = new BalanceSwitchAccountService();

        // 1. Indexing Phase: Create unique identifiers and identify cross-container duplicates
        const mappingIndex = new Map<string, IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI>();
        const ambiguousAccountNumbers = new Set<string>();

        this.traverseAndIndexMapping(this.templateMapping.group, mappingIndex, ambiguousAccountNumbers);

        // 2. Cleaning Phase: Delete all mapped accounts from the template
        this.clearAllUserMappedAccounts(this.templateMapping.group);

        this.applyDuplicateAccountResolutions();

        // 3. Remapping Phase: Map current accounts based on the index
        this.accounts.forEach((newAccount) => {
            const accountNumber = newAccount.accountNumber;

            // Check if the account is in the ignore list (ambiguous mapping across different FLIs)
            if (ambiguousAccountNumbers.has(accountNumber)) {
                return;
            }

            // Check if the account exists in the mapping index
            const destinationContainer = mappingIndex.get(accountNumber);

            if (destinationContainer) {
                // Remap the account to the mapped path
                // Note: If the account appears multiple times in the source accounts list,
                // they will all be mapped to this container, which is the desired behavior for same-FLI duplicates.
                destinationContainer.accounts.push(newAccount);
            }
        });

        // Finalize setup
        this.prepareDuplicateAccountSet();
        this.calculateAll(false);
    }

    /**
     * Traverses the template group structure to index where accounts are mapped.
     */
    private traverseAndIndexMapping(
        group: IAccountMappingTemplateGroup,
        mappingIndex: Map<string, IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI>,
        ambiguousAccountNumbers: Set<string>
    ): void {
        for (const item of group.sortedItems) {
            if (isGroup(item)) {
                this.traverseAndIndexMapping(item as IAccountMappingTemplateGroup, mappingIndex, ambiguousAccountNumbers);
            } else if (isFli(item)) {
                const fli = item as IAccountMappingTemplateGroupItemFLI;

                // Index accounts mapped directly to the FLI
                this.indexAccountsInContainer(fli, mappingIndex, ambiguousAccountNumbers);

                // Index accounts mapped to SubFLIs
                const subFlis = this.getSubFlisOfFli(fli);
                subFlis.forEach((subFli) => {
                    this.indexAccountsInContainer(subFli, mappingIndex, ambiguousAccountNumbers);
                });
            }
        }
    }

    /**
     * Helper to process accounts within a specific container (FLI or SubFLI).
     * Validates if the account is mapped to a different container than previously seen.
     */
    private indexAccountsInContainer(
        container: IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI,
        mappingIndex: Map<string, IAccountMappingTemplateGroupItemFLI | IAccountMappingSubFLI>,
        ambiguousAccountNumbers: Set<string>
    ): void {
        if (!container.accounts) return;

        container.accounts.forEach((acc) => {
            // Filter strictly for user accounts (ignore SubFLIs acting as items and special/header accounts)
            if (
                isAccount(acc) &&
                !isSubfli(acc) &&
                acc.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING
            ) {
                const acNum = (acc as IAccount).accountNumber;

                // If already marked ambiguous, skip
                if (ambiguousAccountNumbers.has(acNum)) {
                    return;
                }

                const existingContainer = mappingIndex.get(acNum);

                if (existingContainer) {
                    // It was found before. Check if it is the SAME container.
                    if (existingContainer !== container) {
                        // Mapped to DIFFERENT containers (e.g. FLI A and FLI B). This is ambiguous.
                        mappingIndex.delete(acNum);
                        ambiguousAccountNumbers.add(acNum);
                    }
                    // If existingContainer === container, it is multiple mappings within the same FLI.
                    // This is allowed/valid, so we leave it in the index.
                } else {
                    // First time seeing this account number
                    mappingIndex.set(acNum, container);
                }
            }
        });
    }

    /**
     * Recursively traverses the template and removes all user-mapped accounts.
     * Preserves SubFLI structures and Special Accounts (e.g. Transferred Profit).
     */
    private clearAllUserMappedAccounts(group: IAccountMappingTemplateGroup): void {
        for (const item of group.sortedItems) {
            if (isGroup(item)) {
                this.clearAllUserMappedAccounts(item as IAccountMappingTemplateGroup);
            } else if (isFli(item)) {
                const fli = item as IAccountMappingTemplateGroupItemFLI;

                // 1. Clean SubFLIs first
                const subFlis = this.getSubFlisOfFli(fli);
                subFlis.forEach((subFli) => {
                    // Remove all accounts from SubFLI
                    subFli.accounts = subFli.accounts.filter((acc) => !isAccount(acc));
                });

                // 2. Clean FLI
                fli.accounts = fli.accounts.filter((acc) => {
                    // Keep SubFLIs (as they are structural items inside the account array)
                    if (isSubfli(acc)) return true;
                    // Keep Special Accounts (e.g. Profit/Loss placeholders)
                    if (acc.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING) return true;
                    // Remove everything else (Standard User Accounts)
                    return false;
                });
            }
        }
    }
}
